import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { Calendar } from "primereact/calendar";
import { Card } from "primereact/card";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dialog } from "primereact/dialog";
import { Dropdown } from "primereact/dropdown";
import { FileUpload } from "primereact/fileupload";
import { InputTextarea } from "primereact/inputtextarea";
import { InputText } from "primereact/inputtext";
import { ProgressSpinner } from "primereact/progressspinner";
import { Tag } from "primereact/tag";
import { Tooltip } from "primereact/tooltip";
import { Toast } from "primereact/toast";
import { useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { renderAsync as renderDocxAsync } from "docx-preview";
import { confirmDialog } from "primereact/confirmdialog";
import ActionMenuButton from "../../../components/general/ActionMenuButton";
import { APP_DOCUMENT_FILE_SIZE_IN_BYTES, getErrorTitle, IMAGE_TYPE_EXTENSIONS } from "../../../constants/constants";
import { ROLE_TYPE_CAREGIVER, ROLE_TYPE_FACILITY_WORKER, ROLE_TYPE_MHP, ROLE_TYPE_NURSE, ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { RouterAddress } from "../../../constants/RouterAddress";
import { Binder, BinderComplianceRun, BinderDocument, BinderDocumentAnalysis, BinderDocumentVersion, BinderFolder, BinderFolderNote, BinderShare, BinderType } from "../../../models/binder";
import { ReferenceType } from "../../../models/document";
import { useAuthExperience } from "../../auth/hooks/useAuthExperience";
import StaffDelegationRequirementsForm from "../../home/components/StaffDelegationRequirementsForm";
import { createEmptyStaffDelegationRequirements, getStaffDelegationRequirementsValidationMessage, normalizeStaffDelegationRequirements, StaffDelegationRequirements } from "../../../constants/staffDelegationRequirements";
import { BRAND_KEY_AFH_HUB, BRAND_KEY_ONE_HUB_FOR_CARE } from "../../../constants/brandConstants";
import { openUrlInNewTab, sanitizeRichHtml } from "../../../utils/securityUtils";
import {
  appAccentPanelStyle,
  appConfirmDialogClasses,
  appDataTableWrapperStyle,
  appDialogContentStyle,
  appDialogHeaderStyle,
  appDialogStyle,
  appFormSectionStyle,
  appMutedTextStyle,
  appNeutralPanelStyle,
  appSectionCardStyle,
  renderConfirmMessage,
  renderSurfaceHeader,
} from "../../../components/general/uiSurfaceStyles";

const ENABLE_BINDER_AI_REVIEW_UI = false;
const BinderPage = () => {
  const authExperience = useAuthExperience();
  const canUseEfax = authExperience.featureFlags.providerTools.eFax;
  const location = useLocation();
  const navigate = useNavigate();
  const { homeId, residentId, shareId } = useParams();
  const { authStore, backendAPIStore } = useContext(MobXProviderContext);
  const toast = useRef<Toast>(null);
  const folderFilesSectionRef = useRef<HTMLDivElement>(null);
  const complianceResultsSectionRef = useRef<HTMLDivElement>(null);
  const docxPreviewContainerRef = useRef<HTMLDivElement>(null);
  const docxPreviewStyleContainerRef = useRef<HTMLDivElement>(null);
  const fileUploadRef = useRef<FileUpload>(null);
  const versionFileUploadRef = useRef<FileUpload>(null);

  const [loading, setLoading] = useState(true);
  const [bootstrapping, setBootstrapping] = useState(false);
  const [runningCompliance, setRunningCompliance] = useState(false);
  const [binder, setBinder] = useState<Binder | null>(null);
  const [facilityTypeOptions, setFacilityTypeOptions] = useState<{ value: string; label: string }[]>(() => (
    (Array.isArray(authExperience.facilityTypeOptions) ? authExperience.facilityTypeOptions : [])
      .filter((option) => !["home_care_agency", "custom"].includes(String(option?.value || "").trim().toLowerCase()))
  ));
  const [selectedFacilityType, setSelectedFacilityType] = useState(
    authExperience.brandKey === BRAND_KEY_AFH_HUB ? "afh" : authExperience.providerSignup.defaultFacilityType || "afh",
  );
  const [showBinderConfigDialog, setShowBinderConfigDialog] = useState(false);
  const [resetFacilityType, setResetFacilityType] = useState("");
  const [resettingBinderConfig, setResettingBinderConfig] = useState(false);
  const [folders, setFolders] = useState<BinderFolder[]>([]);
  const [binderDocuments, setBinderDocuments] = useState<BinderDocument[]>([]);
  const [latestComplianceRun, setLatestComplianceRun] = useState<BinderComplianceRun | null>(null);
  const [showUploadDialog, setShowUploadDialog] = useState(false);
  const [uploadingDocument, setUploadingDocument] = useState(false);
  const [selectedFolderId, setSelectedFolderId] = useState("");
  const [selectedUploadDocumentType, setSelectedUploadDocumentType] = useState("");
  const [uploadDocumentTitle, setUploadDocumentTitle] = useState("");
  const [uploadDocumentNumber, setUploadDocumentNumber] = useState("");
  const [uploadDateOfBirth, setUploadDateOfBirth] = useState<Date | null>(null);
  const [uploadEffectiveDate, setUploadEffectiveDate] = useState<Date | null>(null);
  const [uploadExpirationDate, setUploadExpirationDate] = useState<Date | null>(null);
  const [selectedUploadFile, setSelectedUploadFile] = useState<File | null>(null);
  const [showCreateFolderDialog, setShowCreateFolderDialog] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderRequired, setNewFolderRequired] = useState(false);
  const [newFolderNotShareable, setNewFolderNotShareable] = useState(false);
  const [showEditFolderDialog, setShowEditFolderDialog] = useState(false);
  const [editingFolder, setEditingFolder] = useState<BinderFolder | null>(null);
  const [editFolderRequired, setEditFolderRequired] = useState(true);
  const [editFolderPrivate, setEditFolderPrivate] = useState(false);
  const [savingFolderProperties, setSavingFolderProperties] = useState(false);
  const [activeFolderId, setActiveFolderId] = useState("");
  const [activeFolderDocuments, setActiveFolderDocuments] = useState<BinderDocument[]>([]);
  const [folderSearchQuery, setFolderSearchQuery] = useState("");
  const [folderSort, setFolderSort] = useState<{ field: "name" | "required" | "private"; direction: 1 | -1 }>({ field: "name", direction: 1 });
  const [folderTooltipRefreshKey, setFolderTooltipRefreshKey] = useState(0);
  const [folderPageFirst, setFolderPageFirst] = useState(0);
  const [documentVersionsById, setDocumentVersionsById] = useState<Record<string, BinderDocumentVersion[]>>({});
  const [recordName, setRecordName] = useState("");
  const [loadingFolderFiles, setLoadingFolderFiles] = useState(false);
  const [showVersionDialog, setShowVersionDialog] = useState(false);
  const [uploadingVersion, setUploadingVersion] = useState(false);
  const [selectedVersionDocument, setSelectedVersionDocument] = useState<BinderDocument | null>(null);
  const [selectedVersionFile, setSelectedVersionFile] = useState<File | null>(null);
  const [showSendFaxDialog, setShowSendFaxDialog] = useState(false);
  const [selectedFaxDocument, setSelectedFaxDocument] = useState<BinderDocument | null>(null);
  const [selectedFaxVersion, setSelectedFaxVersion] = useState<BinderDocumentVersion | null>(null);
  const [faxNumber, setFaxNumber] = useState("");
  const [sendingFax, setSendingFax] = useState(false);
  const [showVersionHistoryDialog, setShowVersionHistoryDialog] = useState(false);
  const [selectedVersionHistoryDocument, setSelectedVersionHistoryDocument] = useState<BinderDocument | null>(null);
  const [selectedVersionHistoryRows, setSelectedVersionHistoryRows] = useState<BinderDocumentVersion[]>([]);
  const [loadingVersionHistory, setLoadingVersionHistory] = useState(false);
  const [showEditDocumentDialog, setShowEditDocumentDialog] = useState(false);
  const [savingDocumentChanges, setSavingDocumentChanges] = useState(false);
  const [selectedEditDocument, setSelectedEditDocument] = useState<BinderDocument | null>(null);
  const [editDocumentTitle, setEditDocumentTitle] = useState("");
  const [editDocumentNumber, setEditDocumentNumber] = useState("");
  const [editDateOfBirth, setEditDateOfBirth] = useState<Date | null>(null);
  const [editDocumentFolderId, setEditDocumentFolderId] = useState("");
  const [editDocumentType, setEditDocumentType] = useState("");
  const [editEffectiveDate, setEditEffectiveDate] = useState<Date | null>(null);
  const [editExpirationDate, setEditExpirationDate] = useState<Date | null>(null);
  const [showReviewDocumentDialog, setShowReviewDocumentDialog] = useState(false);
  const [savingReviewDocument, setSavingReviewDocument] = useState(false);
  const [selectedReviewDocument, setSelectedReviewDocument] = useState<BinderDocument | null>(null);
  const [reviewDocumentStatus, setReviewDocumentStatus] = useState("");
  const [reviewDocumentNote, setReviewDocumentNote] = useState("");
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewTitle, setPreviewTitle] = useState("");
  const [docViewerNonImageUrl, setDocViewerNonImageUrl] = useState("");
  const [docViewerImageUrl, setDocViewerImageUrl] = useState("");
  const [docViewerHtmlContent, setDocViewerHtmlContent] = useState("");
  const [docViewerSourceUrl, setDocViewerSourceUrl] = useState("");
  const [docxPreviewBlob, setDocxPreviewBlob] = useState<Blob | null>(null);
  const [loadingDocxPreview, setLoadingDocxPreview] = useState(false);
  const [showDocViewer, setShowDocViewer] = useState(false);
  const [previewRenderKey, setPreviewRenderKey] = useState(0);
  const [showComplianceResults, setShowComplianceResults] = useState(false);
  const [runningAnalysisByDocumentId, setRunningAnalysisByDocumentId] = useState<Record<string, boolean>>({});
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [loadingAnalysisDialog, setLoadingAnalysisDialog] = useState(false);
  const [selectedAnalysisDocument, setSelectedAnalysisDocument] = useState<BinderDocument | null>(null);
  const [selectedAnalyses, setSelectedAnalyses] = useState<BinderDocumentAnalysis[]>([]);
  const [binderLatestAnalyses, setBinderLatestAnalyses] = useState<BinderDocumentAnalysis[]>([]);
  const [loadingBinderAnalysisWatchlist, setLoadingBinderAnalysisWatchlist] = useState(false);
  const [showBinderAnalysisWatchlist, setShowBinderAnalysisWatchlist] = useState(false);
  const [binderShares, setBinderShares] = useState<BinderShare[]>([]);
  const [sharedBinderShare, setSharedBinderShare] = useState<BinderShare | null>(null);
  const [showShareDialog, setShowShareDialog] = useState(false);
  const [showNewShareForm, setShowNewShareForm] = useState(false);
  const [showFolderNoteDialog, setShowFolderNoteDialog] = useState(false);
  const [selectedNoteFolder, setSelectedNoteFolder] = useState<BinderFolder | null>(null);
  const [folderNoteDraft, setFolderNoteDraft] = useState("");
  const [savingFolderNote, setSavingFolderNote] = useState(false);
  const [sharingBinder, setSharingBinder] = useState(false);
  const [shareRecipientEmail, setShareRecipientEmail] = useState("");
  const [shareRecipientName, setShareRecipientName] = useState("");
  const [shareMessage, setShareMessage] = useState("");
  const [shareDialogStep, setShareDialogStep] = useState<1 | 2>(1);
  const [selectedShareFolderIds, setSelectedShareFolderIds] = useState<string[]>([]);
  const [shareExpirationOption, setShareExpirationOption] = useState("7");
  const [editingShare, setEditingShare] = useState<BinderShare | null>(null);
  const [showEditShareFoldersDialog, setShowEditShareFoldersDialog] = useState(false);
  const [savingShareFolders, setSavingShareFolders] = useState(false);
  const [loadingBinderShares, setLoadingBinderShares] = useState(false);
  const [sharedResidentBinderAccessDenied, setSharedResidentBinderAccessDenied] = useState(false);
  const [reviewNotes, setReviewNotes] = useState("");
  const [providerJobQuestions, setProviderJobQuestions] = useState("");
  const [savedJobQuestionTemplates, setSavedJobQuestionTemplates] = useState<any[]>([]);
  const [selectedJobQuestionTemplateId, setSelectedJobQuestionTemplateId] = useState("");
  const [showSaveJobQuestionTemplateDialog, setShowSaveJobQuestionTemplateDialog] = useState(false);
  const [jobQuestionTemplateName, setJobQuestionTemplateName] = useState("");
  const [savingJobQuestionTemplate, setSavingJobQuestionTemplate] = useState(false);
  const [savingSharedReview, setSavingSharedReview] = useState(false);
  const [savingJobQuestions, setSavingJobQuestions] = useState(false);
  const [showAnswerQuestionsDialog, setShowAnswerQuestionsDialog] = useState(false);
  const [selectedQuestionsShare, setSelectedQuestionsShare] = useState<BinderShare | null>(null);
  const [jobQuestionAnswersDraft, setJobQuestionAnswersDraft] = useState("");
  const [savingJobQuestionAnswers, setSavingJobQuestionAnswers] = useState(false);
  const [showShareHistoryDialog, setShowShareHistoryDialog] = useState(false);
  const [showStaffCredentialsDialog, setShowStaffCredentialsDialog] = useState(false);
  const [staffCredentials, setStaffCredentials] = useState<StaffDelegationRequirements>(createEmptyStaffDelegationRequirements);
  const [staffCredentialsName, setStaffCredentialsName] = useState("");
  const [staffCredentialsEmail, setStaffCredentialsEmail] = useState("");
  const [savingStaffCredentials, setSavingStaffCredentials] = useState(false);
  const [showCredentialValidationErrors, setShowCredentialValidationErrors] = useState(false);
  const [selectedHistoryShare, setSelectedHistoryShare] = useState<BinderShare | null>(null);
  const caregiverJobBinderPath = RouterAddress.CAREGIVER_JOB_BINDER_PAGE.replace(/\/$/, "");
  const facilityWorkerJobBinderPath = RouterAddress.FACILITY_WORKER_JOB_BINDER_PAGE.replace(/\/$/, "");
  const nurseJobBinderPath = RouterAddress.NURSE_JOB_BINDER_PAGE.replace(/\/$/, "");
  const mhpJobBinderPath = RouterAddress.MHP_JOB_BINDER_PAGE.replace(/\/$/, "");
  const normalizedPathname = location.pathname.replace(/\/$/, "");
  const isFacilityWorkerJobBinder = normalizedPathname === facilityWorkerJobBinderPath;
  const isCaregiverJobBinder = [caregiverJobBinderPath, facilityWorkerJobBinderPath, nurseJobBinderPath, mhpJobBinderPath].includes(normalizedPathname);
  const professionalRole = normalizedPathname === nurseJobBinderPath
    ? ROLE_TYPE_NURSE
    : normalizedPathname === mhpJobBinderPath
      ? ROLE_TYPE_MHP
      : isFacilityWorkerJobBinder
        ? ROLE_TYPE_FACILITY_WORKER
        : ROLE_TYPE_CAREGIVER;
  const isSharedBinder = !!shareId;
  const isSharedResidentBinder = location.pathname.startsWith("/shared/home/") && location.pathname.includes("/binder/");
  const userId = authStore?.getUserId?.() || "";
  const binderType = isCaregiverJobBinder
    ? BinderType.CAREGIVER_JOB_SEARCH
    : residentId
      ? BinderType.RESIDENT
      : BinderType.HOME;
  const referenceType = isCaregiverJobBinder
    ? ReferenceType.USER
    : residentId
      ? ReferenceType.RESIDENT
      : ReferenceType.BUSINESS_HOME;
  const referenceId = isCaregiverJobBinder ? userId : residentId || homeId || "";
  const binderFacilityTypeLabel = facilityTypeOptions.find((option) => option.value === String(binder?.facilityType || selectedFacilityType))?.label
    || authExperience.facilityTypeOptions.find((option) => option.value === String(binder?.facilityType || selectedFacilityType))?.label
    || authExperience.vocabulary.facilitySingular;

  const openStaffCredentialsDialog = async () => {
    try {
      setShowCredentialValidationErrors(false);
      await backendAPIStore.init();
      const response: any = await backendAPIStore.getStaffAccount(userId);
      setStaffCredentials(normalizeStaffDelegationRequirements(response?.staffDetails || response?.staff?.delegationRequirements));
      setStaffCredentialsName(`${response?.profile?.firstName || ""} ${response?.profile?.lastName || ""}`.trim() || response?.profile?.email || "Staff");
      setStaffCredentialsEmail(String(response?.profile?.email || "").trim());
      setShowStaffCredentialsDialog(true);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load staff credentials.", life: 3500 });
    }
  };

  const saveStaffCredentials = async () => {
    try {
      setShowCredentialValidationErrors(true);
      const validationMessage = getStaffDelegationRequirementsValidationMessage(staffCredentials);
      if (validationMessage) {
        toast.current?.show({ severity: "error", summary: "Validation required", detail: validationMessage, life: 4500 });
        return;
      }
      setSavingStaffCredentials(true);
      await backendAPIStore.init();
      await backendAPIStore.saveStaffAccount({ roleType: ROLE_TYPE_CAREGIVER, staffId: userId, name: staffCredentialsName, email: staffCredentialsEmail, staffDetails: staffCredentials });
      toast.current?.show({ severity: "success", summary: "Credentials saved", detail: "Staff credential details were updated.", life: 2500 });
      setShowStaffCredentialsDialog(false);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save staff credentials.", life: 3500 });
    } finally {
      setSavingStaffCredentials(false);
    }
  };
  const pageTitle = isSharedBinder
    ? "Binder"
    : isCaregiverJobBinder
      ? "Binder"
      : binderType === BinderType.RESIDENT
        ? "Resident Binder"
        : authExperience.brandKey === BRAND_KEY_ONE_HUB_FOR_CARE
          ? "Facility Binder"
          : "Home Binder";
  const resolvedPageTitle = isSharedBinder ? pageTitle : (isCaregiverJobBinder ? pageTitle : (recordName ? `${recordName} Binder` : pageTitle));
  const documentsPagePath = isSharedResidentBinder
    ? RouterAddress.getSharedResidentProfilePagePath(homeId || "", residentId || "")
    : isCaregiverJobBinder
      ? RouterAddress.getUserDocumentsPagePath(userId)
      : residentId && homeId
        ? RouterAddress.getResidentDocumentsPagePath(homeId, residentId)
        : RouterAddress.getHomeDocumentsPagePath(homeId);
  const loadBinder = async () => {
    if (!referenceId && !shareId) {
      return;
    }

    setLoading(true);
    setSharedResidentBinderAccessDenied(false);
    try {
      await backendAPIStore.init();
      const binderResponse = isSharedBinder
        ? await backendAPIStore.getBinderShare(shareId || "")
        : await backendAPIStore.getBinderByReference(referenceType, referenceId);
      setBinder(binderResponse?.binder || null);
      if (!isSharedBinder) {
        setRecordName(String(binderResponse?.referenceName || "").trim());
      }
      if (isCaregiverJobBinder && binderResponse?.binder?.facilityType) {
        setSelectedFacilityType(String(binderResponse.binder.facilityType));
      }
      setFolders(binderResponse?.folders || []);
      setBinderDocuments(binderResponse?.documents || []);
      if (!activeFolderId || !binderResponse?.folders?.some((folder: BinderFolder) => folder.folderId === activeFolderId)) {
        const firstFolderId = binderResponse?.folders?.[0]?.folderId || "";
        setActiveFolderId(firstFolderId);
        setActiveFolderDocuments(firstFolderId
          ? (binderResponse?.documents || []).filter((document: BinderDocument) => document.folderId === firstFolderId)
          : []);
      }
      setLatestComplianceRun(binderResponse?.latestComplianceRun || null);
      setSharedBinderShare(binderResponse?.share || null);
      setReviewNotes(String(binderResponse?.share?.reviewNotes || ""));
      setProviderJobQuestions(String(binderResponse?.share?.jobQuestions || ""));
      if (binderResponse?.binder?.binderId) {
        setLoadingBinderAnalysisWatchlist(true);
        try {
          // const analyses = await backendAPIStore.listBinderAnalysesByBinderId(binderResponse.binder.binderId, true);
          // setBinderLatestAnalyses(analyses || []);
          setBinderLatestAnalyses([]);
        } finally {
          setLoadingBinderAnalysisWatchlist(false);
        }
      } else {
        setBinderLatestAnalyses([]);
      }
    } catch (error: any) {
      const normalizedMessage = String(error?.message || "").trim().toLowerCase();
      if (isSharedResidentBinder && normalizedMessage.includes("permission denied")) {
        setSharedResidentBinderAccessDenied(true);
        setBinder(null);
        setFolders([]);
        setBinderDocuments([]);
        setLatestComplianceRun(null);
        setSharedBinderShare(null);
        setRecordName("");
      }
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load binder.", life: 4000 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isSharedBinder) {
      return;
    }
    const loadFacilityTypes = async () => {
      try {
        const response = await backendAPIStore.listBinderFacilityTypes();
        const apiOptions = (Array.isArray(response) ? response : [])
          .filter((option: any) => !["home_care_agency", "custom"].includes(String(option?.value || "").trim().toLowerCase()));
        const brandOptions = (Array.isArray(authExperience.facilityTypeOptions) ? authExperience.facilityTypeOptions : [])
          .filter((option: any) => !["home_care_agency", "custom"].includes(String(option?.value || "").trim().toLowerCase()));
        const options = (isFacilityWorkerJobBinder && brandOptions.length ? brandOptions : apiOptions.length ? apiOptions : brandOptions);
        const fallbackFacilityType = String(authExperience.providerSignup.defaultFacilityType || "afh").trim().toLowerCase();
        const resolvedOptions = options.length ? options : [{ value: fallbackFacilityType, label: fallbackFacilityType.toUpperCase() }];
        setFacilityTypeOptions(resolvedOptions);
        setSelectedFacilityType((current) => resolvedOptions.some((option: any) => option.value === current) ? current : resolvedOptions[0].value);
      } catch {
        const fallbackOptions = (Array.isArray(authExperience.facilityTypeOptions) ? authExperience.facilityTypeOptions : [])
          .filter((option: any) => !["home_care_agency", "custom"].includes(String(option?.value || "").trim().toLowerCase()));
        const fallbackFacilityType = String(authExperience.providerSignup.defaultFacilityType || "afh").trim().toLowerCase();
        const resolvedOptions = fallbackOptions.length ? fallbackOptions : [{ value: fallbackFacilityType, label: fallbackFacilityType.toUpperCase() }];
        setFacilityTypeOptions(resolvedOptions);
        setSelectedFacilityType((current) => resolvedOptions.some((option: any) => option.value === current) ? current : resolvedOptions[0].value);
      }
    };
    void loadFacilityTypes();
  }, [authExperience.facilityTypeOptions, authExperience.providerSignup.defaultFacilityType, backendAPIStore, isFacilityWorkerJobBinder, isSharedBinder]);

  const formatDateInPacific = (date: Date) => {
    const formatter = new Intl.DateTimeFormat("en-CA", {
      timeZone: "America/Los_Angeles",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    return formatter.format(date);
  };

  const getCalendarDateFromStoredValue = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return null;
    }

    const [yearValue, monthValue, dayValue] = normalizedValue.split("-").map((item) => Number(item));
    if (!yearValue || !monthValue || !dayValue) {
      return null;
    }

    return new Date(yearValue, monthValue - 1, dayValue, 12, 0, 0, 0);
  };

  const getStoredDateFromCalendarValue = (value: Date | Date[] | null | undefined) => {
    const nextValue = Array.isArray(value) ? value[0] : value;
    if (!nextValue) {
      return "";
    }
    return formatDateInPacific(nextValue);
  };

  const getTodayInPacific = () => formatDateInPacific(new Date());

  const loadCurrentUserJobQuestionTemplates = async () => {
    try {
      const preferenceResponse = await backendAPIStore.getCurrentUserPreferences();
      const templates = Array.isArray(preferenceResponse?.binderJobQuestionTemplates)
        ? preferenceResponse.binderJobQuestionTemplates
        : [];
      setSavedJobQuestionTemplates(templates);
    } catch {
      setSavedJobQuestionTemplates([]);
    }
  };

  useEffect(() => {
    void loadBinder();
  }, [isSharedBinder, referenceId, referenceType, shareId]);

  useEffect(() => {
    if (isSharedBinder && authStore.getUserRoleType() === ROLE_TYPE_PROVIDER) {
      void loadCurrentUserJobQuestionTemplates();
      return;
    }
    setSavedJobQuestionTemplates([]);
    setSelectedJobQuestionTemplateId("");
  }, [authStore, isSharedBinder]);

  useEffect(() => {
    const handleOpenAddFolder = () => {
      if (binder && !isSharedBinder && !isCaregiverJobBinder) {
        setShowCreateFolderDialog(true);
      }
    };

    window.addEventListener("binder:open-add-folder", handleOpenAddFolder);
    return () => {
      window.removeEventListener("binder:open-add-folder", handleOpenAddFolder);
    };
  }, [binder, isCaregiverJobBinder, isSharedBinder]);

  useEffect(() => {
    const loadShares = async () => {
      if (!binder?.binderId || isSharedBinder) {
        setBinderShares([]);
        return;
      }

      setLoadingBinderShares(true);
      try {
        const shares = await backendAPIStore.listBinderSharesByBinderId(binder.binderId);
        setBinderShares(shares || []);
      } catch {
        setBinderShares([]);
      } finally {
        setLoadingBinderShares(false);
      }
    };

    void loadShares();
  }, [backendAPIStore, binder?.binderId, isCaregiverJobBinder, isSharedBinder]);

  useEffect(() => {
    const nextOptions = getDocumentTypeOptionsForFolder(selectedFolderId);
    if (!nextOptions.length) {
      if (selectedUploadDocumentType) {
        setSelectedUploadDocumentType("");
      }
      return;
    }

    const hasCurrentSelection = nextOptions.some((option) => option.value === selectedUploadDocumentType);
    if (!hasCurrentSelection) {
      setSelectedUploadDocumentType(String(nextOptions[0]?.value || ""));
    }
  }, [folders, selectedFolderId]);

  useEffect(() => {
    if (!selectedUploadDocumentType) {
      return;
    }
    if (!uploadDocumentTitle.trim()) {
      setUploadDocumentTitle(selectedUploadDocumentType);
    }
  }, [selectedUploadDocumentType, uploadDocumentTitle]);

  useEffect(() => {
    if (!docxPreviewBlob || !docxPreviewContainerRef.current) {
      return;
    }

    let cancelled = false;
    const bodyContainer = docxPreviewContainerRef.current;
    const styleContainer = docxPreviewStyleContainerRef.current;
    bodyContainer.innerHTML = "";
    if (styleContainer) {
      styleContainer.innerHTML = "";
    }
    setLoadingDocxPreview(true);

    void renderDocxAsync(docxPreviewBlob, bodyContainer, styleContainer || undefined, {
      inWrapper: true,
      hideWrapperOnPrint: false,
      ignoreWidth: false,
      ignoreHeight: false,
      ignoreFonts: false,
      breakPages: true,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
      ignoreLastRenderedPageBreak: false,
      useBase64URL: true,
      renderChanges: false,
      renderComments: true,
      renderAltChunks: true,
    })
      .catch((error: any) => {
        if (!cancelled) {
          setDocxPreviewBlob(null);
          toast.current?.show({ severity: "error", summary: "Preview unavailable", detail: error?.message || "Unable to render this Word document.", life: 4000 });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingDocxPreview(false);
        }
      });

    return () => {
      cancelled = true;
      bodyContainer.innerHTML = "";
      if (styleContainer) {
        styleContainer.innerHTML = "";
      }
    };
  }, [docxPreviewBlob]);

  const onBootstrapBinder = async () => {
    setBootstrapping(true);
    try {
      await backendAPIStore.bootstrapBinder({
        binderType,
        referenceType,
        referenceId,
        ...(isCaregiverJobBinder ? { facilityType: selectedFacilityType, professionalRole } : {}),
      });
      await loadBinder();
      toast.current?.show({ severity: "success", summary: "Binder ready", detail: `${resolvedPageTitle} created successfully.`, life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to create binder.", life: 4000 });
    } finally {
      setBootstrapping(false);
    }
  };

  const onResetBinderConfig = async () => {
    if (!binder?.binderId || !resetFacilityType) return;
    setResettingBinderConfig(true);
    try {
      await backendAPIStore.resetBinderConfig({ binderId: binder.binderId, facilityType: resetFacilityType });
      setShowBinderConfigDialog(false);
      setActiveFolderId("");
      await loadBinder();
      toast.current?.show({ severity: "success", summary: "Binder updated", detail: "The old Binder was archived and a new Binder was created.", life: 4000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update Binder configuration.", life: 4000 });
    } finally {
      setResettingBinderConfig(false);
    }
  };

  const onRunCompliance = async () => {
    if (!binder?.binderId) {
      return;
    }

    setRunningCompliance(true);
    try {
      const response = await backendAPIStore.runBinderCompliance(binder.binderId);
      setLatestComplianceRun(response || null);
      setShowComplianceResults(true);
      toast.current?.show({ severity: "success", summary: "Compliance queued", detail: "Binder compliance check has been recorded.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to run compliance.", life: 4000 });
    } finally {
      setRunningCompliance(false);
    }
  };

  const getAnalysisSeverity = (status?: string) => {
    if (status === "completed") {
      return "success";
    }
    if (status === "failed") {
      return "danger";
    }
    if (status === "stale") {
      return "warning";
    }
    if (status === "running") {
      return "warning";
    }
    return "info";
  };

  const getAnalysisLabel = (status?: string) => {
    if (!status) {
      return "Not analyzed";
    }
    if (status === "stale") {
      return "Stale";
    }
    return status.replace(/_/g, " ").replace(/\b\w/g, (character) => character.toUpperCase());
  };

  const getFolderMatchLabel = (value?: string) => {
    if (!value) {
      return "Review needed";
    }
    if (value === "yes") {
      return "Yes";
    }
    if (value === "no") {
      return "No";
    }
    return "Review needed";
  };

  const getFolderMatchSeverity = (value?: string) => {
    if (value === "yes") {
      return "success";
    }
    if (value === "no") {
      return "danger";
    }
    return "warning";
  };

  const onRunDocumentAnalysis = async (binderDocument: BinderDocument) => {
    const binderDocumentId = binderDocument.binderDocumentId || "";
    if (!binderDocumentId) {
      return;
    }

    setRunningAnalysisByDocumentId((current) => ({ ...current, [binderDocumentId]: true }));
    try {
      const analysis = await backendAPIStore.runBinderDocumentAnalysis(binderDocumentId);
      await loadBinder();
      if (activeFolderId) {
        await loadFolderFiles(activeFolderId);
      }
      const isRunningAsync = analysis?.status === "running";
      toast.current?.show({
        severity: isRunningAsync ? "info" : "success",
        summary: isRunningAsync ? "Analysis started" : "Analysis complete",
        detail: isRunningAsync
          ? "PDF analysis is running in the background with Textract. Check back in a moment for results."
          : "Latest binder document version analyzed successfully.",
        life: 4000,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to analyze this binder document.", life: 4000 });
    } finally {
      setRunningAnalysisByDocumentId((current) => ({ ...current, [binderDocumentId]: false }));
    }
  };

  const openAnalysisDialog = async (binderDocument: BinderDocument) => {
    if (!binderDocument?.binderDocumentId) {
      toast.current?.show({
        severity: "warn",
        summary: "Analysis unavailable",
        detail: "This binder document is missing its document id, so AI analysis cannot be opened.",
        life: 3500,
      });
      return;
    }

    setSelectedAnalysisDocument(binderDocument);
    setSelectedAnalyses([]);
    setShowAnalysisDialog(true);
    setLoadingAnalysisDialog(true);
    try {
      const analyses = await backendAPIStore.listBinderDocumentAnalyses(binderDocument.binderDocumentId);
      setSelectedAnalyses(analyses || []);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load binder analyses.", life: 4000 });
    } finally {
      setLoadingAnalysisDialog(false);
    }
  };

  const renderFolderRequirement = (rowData: BinderFolder) =>
    rowData.requiredForCompliance ? <Tag value="Required" severity="warning" /> : <Tag value="Optional" severity="info" />;

  const folderOptions = folders.map((folder) => ({
    label: folder.folderName || "Untitled folder",
    value: folder.folderId || "",
  }));

  const getFolderName = (folderId?: string) =>
    folders.find((folder) => folder.folderId === folderId)?.folderName || "Unknown folder";

  const isIdentityWorkEligibilityFolder = (folder?: BinderFolder | null) =>
    /(id[_\s-]*work|identity[_\s-]*work|work[_\s-]*eligibility)/i.test(
      `${folder?.folderCode || ""} ${folder?.folderName || ""}`,
    );

  const getDocumentTypeOptionsForFolder = (folderId?: string) =>
    (folders.find((folder) => folder.folderId === folderId)?.documentTypeOptions || []).map((option) => ({
      label: option,
      value: option,
    }));

  const activeFolder = folders.find((folder) => folder.folderId === activeFolderId) || null;
  const selectedUploadFolder = folders.find((folder) => folder.folderId === selectedFolderId) || null;
  const selectedEditFolder = folders.find((folder) => folder.folderId === editDocumentFolderId) || null;
  const normalizedFolderSearchQuery = folderSearchQuery.trim().toLowerCase();
  const visibleFolders = folders.filter((folder) =>
    !normalizedFolderSearchQuery || String(folder.folderName || "").toLowerCase().includes(normalizedFolderSearchQuery),
  );
  const sortedVisibleFolders = [...visibleFolders].sort((left, right) => {
    if (folderSort.field === "name") {
      return String(left.folderName || "").localeCompare(String(right.folderName || ""), undefined, { sensitivity: "base" }) * folderSort.direction;
    }
    const leftValue = folderSort.field === "required" ? left.requiredForCompliance === true : left.shareable === false;
    const rightValue = folderSort.field === "required" ? right.requiredForCompliance === true : right.shareable === false;
    if (leftValue === rightValue) {
      return String(left.folderName || "").localeCompare(String(right.folderName || ""), undefined, { sensitivity: "base" });
    }
    return (leftValue ? -1 : 1) * folderSort.direction;
  });

  const toggleFolderSort = (field: "name" | "required" | "private") => {
    setFolderPageFirst(0);
    setFolderSort((currentSort) => currentSort.field === field
      ? { field, direction: currentSort.direction === 1 ? -1 : 1 }
      : { field, direction: 1 });
  };

  const folderSortIcon = (field: "name" | "required" | "private") => (
    folderSort.field === field
      ? (folderSort.direction === 1 ? "pi pi-sort-amount-up" : "pi pi-sort-amount-down")
      : "pi pi-sort-alt"
  );

  const formatDateTime = (value?: string) => {
    if (!value) {
      return "Not available";
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return value;
    }

    return parsedDate.toLocaleString("en-US", {
      timeZone: "America/Los_Angeles",
    });
  };

  const formatStoredDate = (value?: string) => {
    const normalizedValue = String(value || "").trim();
    if (!normalizedValue) {
      return "Not set";
    }

    const parsedDate = getCalendarDateFromStoredValue(normalizedValue);
    if (!parsedDate) {
      return normalizedValue;
    }

    return parsedDate.toLocaleDateString("en-US", { timeZone: "America/Los_Angeles" });
  };

  const getExpiryStatus = (expirationDate?: string) => {
    const normalizedValue = String(expirationDate || "").trim();
    if (!normalizedValue) {
      return { label: "No expiry", severity: "info" as const };
    }

    const todayInPacific = getTodayInPacific();
    if (normalizedValue < todayInPacific) {
      return { label: "Expired", severity: "danger" as const };
    }
    if (normalizedValue <= formatDateInPacific(new Date(Date.now() + (1000 * 60 * 60 * 24 * 30)))) {
      return { label: "Expiring soon", severity: "warning" as const };
    }
    return { label: "Valid", severity: "success" as const };
  };

  const expiredBinderDocuments = binderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expired");
  const expiringSoonBinderDocuments = binderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expiring soon");
  const binderExpiryAttentionCount = expiredBinderDocuments.length + expiringSoonBinderDocuments.length;
  const expiredActiveFolderDocuments = activeFolderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expired");
  const expiringSoonActiveFolderDocuments = activeFolderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expiring soon");
  const activeFolderExpiryAttentionCount = expiredActiveFolderDocuments.length + expiringSoonActiveFolderDocuments.length;
  const missingRequiredFolders = folders.filter((folder) => folder.requiredForCompliance === true
    && !binderDocuments.some((document) => document.folderId === folder.folderId));
  const complianceSeverity = latestComplianceRun?.status === "compliant"
    ? "success"
    : latestComplianceRun?.status === "missing"
      ? "danger"
      : latestComplianceRun?.status === "outdated"
        ? "warning"
        : latestComplianceRun?.status
          ? "info"
          : "info";
  const complianceFindings = latestComplianceRun?.findings || [];
  const complianceDialogFindings = [...complianceFindings].sort((left: any, right: any) => {
    const leftIsCompliant = String(left.status || "").toLowerCase() === "compliant";
    const rightIsCompliant = String(right.status || "").toLowerCase() === "compliant";
    if (leftIsCompliant !== rightIsCompliant) {
      return leftIsCompliant ? 1 : -1;
    }
    return String(left.folderName || "").localeCompare(String(right.folderName || ""));
  });

  const getFindingSeverity = (status?: string) => {
    if (status === "compliant") {
      return "success";
    }
    if (status === "missing") {
      return "danger";
    }
    if (status === "outdated") {
      return "warning";
    }
    return "info";
  };

  const getFindingLabel = (status?: string) => {
    if (!status) {
      return "Not checked";
    }
    if (status === "review_needed") {
      return "Review needed";
    }
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  const getFolderComplianceFinding = (folder: BinderFolder) =>
    complianceFindings.find((finding) => finding.folderId === folder.folderId || finding.folderCode === folder.folderCode);

  const renderComplianceAiReview = (rowData: any) => {
    if (!rowData?.aiAnalysisStatus) {
      return "No AI review yet";
    }

    const snippets = [
      rowData.aiSummary,
      rowData.aiRecommendedAction,
      rowData.aiReviewReasons?.[0],
    ].filter(Boolean);
    const confidenceLabel = rowData.aiConfidenceScore ? `Confidence ${rowData.aiConfidenceScore}%` : "";
    return [getAnalysisLabel(rowData.aiAnalysisStatus), confidenceLabel, ...snippets].filter(Boolean).join(" | ");
  };

  const getAnalysisDocumentTitle = (analysis: BinderDocumentAnalysis) =>
    binderDocuments.find((document) => document.binderDocumentId === analysis.binderDocumentId)?.title
    || analysis.fileName
    || "Untitled binder file";

  const binderAttentionAnalyses = binderLatestAnalyses.filter((analysis) =>
    analysis.status === "running"
    || analysis.status === "failed"
    || analysis.status === "stale"
    || (analysis.confidenceScore || 0) < 70
    || (analysis.exactRuleMatchConfidence || 0) < 70
    || analysis.likelySatisfiesFolder === "review_needed"
    || analysis.likelySatisfiesFolder === "no"
    || (analysis.reviewReasons || []).length > 0
    || (analysis.missingEvidence || []).length > 0,
  );
  const runningAnalysisCount = binderLatestAnalyses.filter((analysis) => analysis.status === "running").length;
  const failedAnalysisCount = binderLatestAnalyses.filter((analysis) => analysis.status === "failed").length;
  const staleAnalysisCount = binderLatestAnalyses.filter((analysis) => analysis.status === "stale").length;
  const lowConfidenceAnalysisCount = binderLatestAnalyses.filter((analysis) =>
    analysis.status === "completed" && (((analysis.confidenceScore || 0) < 70) || ((analysis.exactRuleMatchConfidence || 0) < 70)),
  ).length;

  const openUploadDialog = (folderId?: string) => {
    setSelectedFolderId(folderId || activeFolderId || folders[0]?.folderId || "");
    setSelectedUploadDocumentType("");
    setUploadDocumentTitle("");
    setUploadDocumentNumber("");
    setUploadDateOfBirth(null);
    setUploadEffectiveDate(null);
    setUploadExpirationDate(null);
    setSelectedUploadFile(null);
    fileUploadRef.current?.clear();
    setShowUploadDialog(true);
  };

  const loadFolderFiles = async (folderId: string) => {
    if (!binder?.binderId) {
      return;
    }

    setLoadingFolderFiles(true);
    try {
      const folderDocuments = await backendAPIStore.listBinderDocuments(binder.binderId, folderId, isSharedBinder ? shareId : undefined);
      setActiveFolderId(folderId);
      setActiveFolderDocuments(folderDocuments || []);

      const versionEntries = await Promise.all(
        (folderDocuments || []).map(async (document: BinderDocument) => {
          if (!document.binderDocumentId) {
            return [document.binderDocumentId || "", []] as const;
          }
          const versions = await backendAPIStore.listBinderDocumentVersions(document.binderDocumentId, isSharedBinder ? shareId : undefined);
          return [document.binderDocumentId, versions || []] as const;
        }),
      );
      setDocumentVersionsById(Object.fromEntries(versionEntries));
      window.setTimeout(() => {
        folderFilesSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 50);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load folder files.", life: 4000 });
    } finally {
      setLoadingFolderFiles(false);
    }
  };

  const onCreateCustomFolder = async () => {
    if (!binder?.binderId) {
      return;
    }

    if (!newFolderName.trim()) {
      toast.current?.show({ severity: "warn", summary: "Folder required", detail: "Enter a custom folder name.", life: 3000 });
      return;
    }

    setCreatingFolder(true);
    try {
      await backendAPIStore.createBinderFolder({
        binderId: binder.binderId,
        folderName: newFolderName.trim(),
        folderCode: newFolderName.trim().toLowerCase().replace(/[^a-z0-9]+/g, "_"),
        folderType: "custom" as any,
        shareable: !newFolderNotShareable,
        requiredForCompliance: newFolderRequired,
      });
      setShowCreateFolderDialog(false);
      setNewFolderName("");
      setNewFolderRequired(false);
      setNewFolderNotShareable(false);
      await loadBinder();
      toast.current?.show({ severity: "success", summary: "Folder created", detail: "Custom binder folder created successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to create folder.", life: 4000 });
    } finally {
      setCreatingFolder(false);
    }
  };

  const openEditFolderDialog = (folder: BinderFolder) => {
    setEditingFolder(folder);
    setEditFolderRequired(folder.requiredForCompliance !== false);
    setEditFolderPrivate(folder.shareable === false);
    setShowEditFolderDialog(true);
  };

  const onSaveFolderProperties = async () => {
    if (!editingFolder?.folderId) {
      return;
    }

    setSavingFolderProperties(true);
    try {
      const updatedFolder = await backendAPIStore.updateBinderFolder({
        ...editingFolder,
        requiredForCompliance: editFolderRequired,
        shareable: !editFolderPrivate,
      });
      setFolders((currentFolders) => currentFolders.map((folder) => (
        folder.folderId === updatedFolder.folderId ? { ...folder, ...updatedFolder } : folder
      )));
      setShowEditFolderDialog(false);
      setEditingFolder(null);
      toast.current?.show({ severity: "success", summary: "Folder updated", detail: "Folder properties updated successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update folder.", life: 4000 });
    } finally {
      setSavingFolderProperties(false);
    }
  };

  const onDeleteCustomFolder = (folder: BinderFolder) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete custom folder",
        `Delete the folder "${folder.folderName}" if it no longer has binder files in it.`,
      ),
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          if (!binder?.binderId || !folder.folderId) {
            return;
          }

          const existingDocuments = await backendAPIStore.listBinderDocuments(binder.binderId, folder.folderId);
          if ((existingDocuments || []).length > 0) {
            toast.current?.show({
              severity: "warn",
              summary: "Folder not empty",
              detail: "Move or remove files from this custom folder before deleting it.",
              life: 4000,
            });
            return;
          }

          await backendAPIStore.deleteBinderFolder(folder.folderId!);
          if (activeFolderId === folder.folderId) {
            setActiveFolderId("");
            setActiveFolderDocuments([]);
            setDocumentVersionsById({});
          }
          await loadBinder();
          toast.current?.show({ severity: "success", summary: "Deleted", detail: "Custom binder folder deleted.", life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to delete folder.", life: 4000 });
        }
      },
    });
  };

  const onFileSelected = (event: { files?: File[] }) => {
    const file = event.files?.[0] || null;
    setSelectedUploadFile(file);
    if (file && !uploadDocumentTitle) {
      const fileNameWithoutExtension = file.name.replace(/\.[^/.]+$/, "");
      setUploadDocumentTitle(fileNameWithoutExtension);
    }
  };

  const onVersionFileSelected = (event: { files?: File[] }) => {
    setSelectedVersionFile(event.files?.[0] || null);
  };

  const getLatestVersion = (binderDocument: BinderDocument) => {
    const versions = documentVersionsById[binderDocument.binderDocumentId || ""] || [];
    return (
      versions.find((version) => version.versionNumber === binderDocument.latestVersionNumber) ||
      versions[0] ||
      null
    );
  };

  const isDocumentMarkedForUpdate = (binderDocument?: BinderDocument | null) => String(binderDocument?.reviewStatus || "").trim().toLowerCase() === "needs_update";

  const getLatestVersionForAction = async (binderDocument: BinderDocument) => {
    const cachedVersion = getLatestVersion(binderDocument);
    if (cachedVersion?.versionId) {
      return cachedVersion;
    }

    if (!binderDocument.binderDocumentId) {
      return null;
    }

    const fetchedVersions = await backendAPIStore.listBinderDocumentVersions(binderDocument.binderDocumentId, isSharedBinder ? shareId : undefined);
    const normalizedVersions = fetchedVersions || [];
    setDocumentVersionsById((previous) => ({
      ...previous,
      [binderDocument.binderDocumentId!]: normalizedVersions,
    }));

    return (
      normalizedVersions.find((version: BinderDocumentVersion) => version.versionNumber === binderDocument.latestVersionNumber) ||
      normalizedVersions[0] ||
      null
    );
  };

  const isSupportedFaxVersion = (version?: BinderDocumentVersion | null) => {
    const fileName = String(version?.fileName || version?.s3Key || "").toLowerCase();
    const mimeType = String(version?.mimeType || "").toLowerCase();
    return [".pdf", ".doc", ".docx", ".txt"].some((extension) => fileName.endsWith(extension))
      || ["application/pdf", "application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document", "text/plain"].some((type) => mimeType.includes(type));
  };

  const openSendFaxDialog = async (binderDocument: BinderDocument) => {
    try {
      const latestVersion = await getLatestVersionForAction(binderDocument);
      if (!latestVersion) {
        toast.current?.show({ severity: "warn", summary: "Fax unavailable", detail: "No current file is available to send.", life: 3000 });
        return;
      }
      if (!isSupportedFaxVersion(latestVersion)) {
        toast.current?.show({ severity: "warn", summary: "File not supported", detail: "Only PDF, Word, and text documents can be sent by fax.", life: 4000 });
        return;
      }
      setSelectedFaxDocument(binderDocument);
      setSelectedFaxVersion(latestVersion);
      setFaxNumber("");
      setShowSendFaxDialog(true);
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to prepare this file for faxing.", life: 4000 });
    }
  };

  const onSendFax = async () => {
    if (!selectedFaxDocument?.binderDocumentId || !selectedFaxVersion?.versionId || !faxNumber.trim()) {
      toast.current?.show({ severity: "warn", summary: "Fax number required", detail: "Enter the number where this document should be sent.", life: 3000 });
      return;
    }
    setSendingFax(true);
    try {
      const response = await backendAPIStore.sendBinderDocumentFax({
        binderDocumentId: selectedFaxDocument.binderDocumentId,
        versionId: selectedFaxVersion.versionId,
        faxNumber,
      });
      setShowSendFaxDialog(false);
      toast.current?.show({ severity: "success", summary: "Fax submitted", detail: response?.message || "The fax has been submitted.", life: 4000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to send fax.", life: 5000 });
    } finally {
      setSendingFax(false);
    }
  };

  const openVersionDialog = (binderDocument: BinderDocument) => {
    setSelectedVersionDocument(binderDocument);
    setSelectedVersionFile(null);
    versionFileUploadRef.current?.clear();
    setShowVersionDialog(true);
  };

  const openVersionHistoryDialog = async (binderDocument: BinderDocument) => {
    setSelectedVersionHistoryDocument(binderDocument);
    setSelectedVersionHistoryRows(documentVersionsById[binderDocument.binderDocumentId || ""] || []);
    setShowVersionHistoryDialog(true);

    if (!binderDocument.binderDocumentId) {
      return;
    }

    setLoadingVersionHistory(true);
    try {
      const versions = await backendAPIStore.listBinderDocumentVersions(binderDocument.binderDocumentId, isSharedBinder ? shareId : undefined);
      setSelectedVersionHistoryRows(versions || []);
      setDocumentVersionsById((previous) => ({
        ...previous,
        [binderDocument.binderDocumentId!]: versions || [],
      }));
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to load binder versions.", life: 4000 });
    } finally {
      setLoadingVersionHistory(false);
    }
  };

  const openEditDocumentDialog = (binderDocument: BinderDocument) => {
    setSelectedEditDocument(binderDocument);
    setEditDocumentTitle(binderDocument.title || "");
    setEditDocumentNumber(binderDocument.documentNumber || "");
    setEditDateOfBirth(getCalendarDateFromStoredValue(binderDocument.dateOfBirth));
    setEditDocumentFolderId(binderDocument.folderId || activeFolderId || folders[0]?.folderId || "");
    setEditDocumentType(binderDocument.documentCategory || "");
    setEditEffectiveDate(getCalendarDateFromStoredValue(binderDocument.effectiveDate));
    setEditExpirationDate(getCalendarDateFromStoredValue(binderDocument.expirationDate));
    setShowEditDocumentDialog(true);
  };

  const onOpenLatestVersion = async (binderDocument: BinderDocument) => {
    try {
      const latestVersion = await getLatestVersionForAction(binderDocument);
      if (!binderDocument.binderDocumentId || !latestVersion?.versionId) {
        toast.current?.show({ severity: "warn", summary: "Version unavailable", detail: "No latest version is available for this binder file.", life: 3000 });
        return;
      }

      const downloadUrl = await backendAPIStore.getBinderDocumentDownloadUrl(binderDocument.binderDocumentId, latestVersion.versionId, isSharedBinder ? shareId : undefined);
      if (downloadUrl) {
        openUrlInNewTab(downloadUrl);
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to open binder file.", life: 4000 });
    }
  };

  const onOpenSpecificVersion = async (binderDocument: BinderDocument, version: BinderDocumentVersion) => {
    try {
      if (!binderDocument.binderDocumentId || !version.versionId) {
        toast.current?.show({ severity: "warn", summary: "Version unavailable", detail: "This binder version is not available.", life: 3000 });
        return;
      }

      const downloadUrl = await backendAPIStore.getBinderDocumentDownloadUrl(binderDocument.binderDocumentId, version.versionId, isSharedBinder ? shareId : undefined);
      if (downloadUrl) {
        openUrlInNewTab(downloadUrl);
      }
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to open binder version.", life: 4000 });
    }
  };

  const onPreviewVersion = async (binderDocument: BinderDocument, version: BinderDocumentVersion) => {
    try {
      if (!binderDocument.binderDocumentId || !version.versionId) {
        toast.current?.show({ severity: "warn", summary: "Preview unavailable", detail: "This binder version is not available for preview.", life: 3000 });
        return;
      }

      setLoadingPreview(true);
      setShowDocViewer(true);
      setPreviewTitle(version.fileName || binderDocument.title || "Binder Preview");
      setDocViewerImageUrl("");
      setDocViewerNonImageUrl("");
      setDocViewerHtmlContent("");
      setDocViewerSourceUrl("");
      setDocxPreviewBlob(null);
      setLoadingDocxPreview(false);
      setPreviewRenderKey((current) => current + 1);

      const downloadUrl = await backendAPIStore.getBinderDocumentDownloadUrl(binderDocument.binderDocumentId, version.versionId, isSharedBinder ? shareId : undefined);
      const fileExtension = version.fileName?.split(".").pop()?.toLowerCase();
      const mimeType = String(version.mimeType || "").toLowerCase();
      if (fileExtension && IMAGE_TYPE_EXTENSIONS.includes(fileExtension)) {
        setDocViewerImageUrl(downloadUrl || "");
        setDocViewerSourceUrl(downloadUrl || "");
      } else if (fileExtension === "pdf" || mimeType.includes("application/pdf")) {
        const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl || "")}&embedded=true&_=${Date.now()}`;
        setDocViewerNonImageUrl(googleDocsViewerUrl);
        setDocViewerSourceUrl(downloadUrl || "");
      } else if (fileExtension === "html" || fileExtension === "htm" || mimeType.includes("text/html")) {
        const htmlResponse = await fetch(downloadUrl || "");
        const htmlContent = await htmlResponse.text();
        setDocViewerHtmlContent(sanitizeRichHtml(htmlContent));
        setDocViewerSourceUrl(downloadUrl || "");
      } else if (fileExtension === "docx" || mimeType.includes("application/vnd.openxmlformats-officedocument.wordprocessingml.document")) {
        const docxResponse = await fetch(downloadUrl || "");
        if (!docxResponse.ok) {
          throw new Error("Unable to load the Word document for preview.");
        }
        setDocxPreviewBlob(await docxResponse.blob());
        setDocViewerSourceUrl(downloadUrl || "");
      } else if (fileExtension === "doc" || mimeType.includes("application/msword")) {
        const docResponse = await fetch(downloadUrl || "");
        const docContent = await docResponse.text();
        if (/<html[\s>]/i.test(docContent) || /<!doctype html/i.test(docContent)) {
          setDocViewerHtmlContent(sanitizeRichHtml(docContent));
          setDocViewerSourceUrl(downloadUrl || "");
        } else {
          const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl || "")}&embedded=true&_=${Date.now()}`;
          setDocViewerNonImageUrl(googleDocsViewerUrl);
          setDocViewerSourceUrl(downloadUrl || "");
        }
      } else {
        const googleDocsViewerUrl = `https://docs.google.com/viewer?url=${encodeURIComponent(downloadUrl || "")}&embedded=true&_=${Date.now()}`;
        setDocViewerNonImageUrl(googleDocsViewerUrl);
        setDocViewerSourceUrl(downloadUrl || "");
      }
    } catch (error: any) {
      setShowDocViewer(false);
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to preview binder file.", life: 4000 });
    } finally {
      setLoadingPreview(false);
    }
  };

  const onUploadBinderFile = async () => {
    if (!binder?.binderId) {
      return;
    }

    if (!selectedFolderId) {
      toast.current?.show({ severity: "warn", summary: "Folder required", detail: "Choose a binder folder first.", life: 3000 });
      return;
    }

    if (!selectedUploadFile) {
      toast.current?.show({ severity: "warn", summary: "File required", detail: "Choose a file to upload.", life: 3000 });
      return;
    }

    setUploadingDocument(true);
    try {
      const createdDocument = await backendAPIStore.createBinderDocument({
        binderId: binder.binderId,
        folderId: selectedFolderId,
        title: uploadDocumentTitle?.trim() || selectedUploadFile.name,
        documentCategory: selectedUploadDocumentType || getFolderName(selectedFolderId),
        documentNumber: uploadDocumentNumber.trim(),
        dateOfBirth: isIdentityWorkEligibilityFolder(selectedUploadFolder) ? getStoredDateFromCalendarValue(uploadDateOfBirth) : "",
        effectiveDate: getStoredDateFromCalendarValue(uploadEffectiveDate),
        expirationDate: getStoredDateFromCalendarValue(uploadExpirationDate),
      } as BinderDocument);

      const extension = selectedUploadFile.name.includes(".")
        ? selectedUploadFile.name.split(".").pop()?.toLowerCase()
        : "";
      const objectKey = `binder/${binder.binderId}/${createdDocument.binderDocumentId}/${uuidv4()}${extension ? `.${extension}` : ""}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(referenceId, referenceType, objectKey);
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedUploadFile);

      await backendAPIStore.createBinderDocumentVersion({
        binderDocumentId: createdDocument.binderDocumentId,
        fileName: selectedUploadFile.name,
        s3Key: objectKey,
        mimeType: selectedUploadFile.type || "application/octet-stream",
      });
      await runComplianceAfterUpload();

      await loadBinder();
      if (selectedFolderId) {
        await loadFolderFiles(selectedFolderId);
      }
      setShowUploadDialog(false);
      fileUploadRef.current?.clear();
      setSelectedUploadDocumentType("");
      setUploadDocumentNumber("");
      toast.current?.show({ severity: "success", summary: "Uploaded", detail: "Binder file uploaded successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to upload binder file.", life: 4000 });
    } finally {
      setUploadingDocument(false);
    }
  };

  const onUploadNewVersion = async () => {
    if (!binder?.binderId || !selectedVersionDocument?.binderDocumentId) {
      return;
    }

    if (!selectedVersionFile) {
      toast.current?.show({ severity: "warn", summary: "File required", detail: "Choose a file to upload as the next version.", life: 3000 });
      return;
    }

    setUploadingVersion(true);
    try {
      const extension = selectedVersionFile.name.includes(".")
        ? selectedVersionFile.name.split(".").pop()?.toLowerCase()
        : "";
      const objectKey = `binder/${binder.binderId}/${selectedVersionDocument.binderDocumentId}/${uuidv4()}${extension ? `.${extension}` : ""}`;
      const presignedUrl = await backendAPIStore.getPresignedUrlForUpload(referenceId, referenceType, objectKey);
      await backendAPIStore.uploadFileToS3(presignedUrl, selectedVersionFile);

      await backendAPIStore.createBinderDocumentVersion({
        binderDocumentId: selectedVersionDocument.binderDocumentId,
        fileName: selectedVersionFile.name,
        s3Key: objectKey,
        mimeType: selectedVersionFile.type || "application/octet-stream",
      });
      await runComplianceAfterUpload();

      if (isDocumentMarkedForUpdate(selectedVersionDocument)) {
        await backendAPIStore.updateBinderDocument({
          binderDocumentId: selectedVersionDocument.binderDocumentId,
          reviewStatus: "",
          reviewNote: "",
          reviewUpdatedAt: new Date().toISOString(),
          reviewUpdatedBy: authStore.getUserName() || authStore.getUserEmailId() || "Hub user",
        } as BinderDocument);
      }

      await loadBinder();
      if (activeFolderId) {
        await loadFolderFiles(activeFolderId);
      }
      setShowVersionDialog(false);
      versionFileUploadRef.current?.clear();
      toast.current?.show({ severity: "success", summary: "New version uploaded", detail: "Binder document version saved successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to upload a new version.", life: 4000 });
    } finally {
      setUploadingVersion(false);
    }
  };

  const onSaveDocumentChanges = async () => {
    if (!selectedEditDocument?.binderDocumentId || !binder?.binderId) {
      return;
    }

    if (!editDocumentTitle.trim()) {
      toast.current?.show({ severity: "warn", summary: "Document title required", detail: "Enter a document title before saving.", life: 3000 });
      return;
    }

    if (!editDocumentFolderId) {
      toast.current?.show({ severity: "warn", summary: "Folder required", detail: "Select a binder folder before saving.", life: 3000 });
      return;
    }

    setSavingDocumentChanges(true);
    try {
      await backendAPIStore.updateBinderDocument({
        ...selectedEditDocument,
        binderId: binder.binderId,
        title: editDocumentTitle.trim(),
        documentNumber: editDocumentNumber.trim(),
        dateOfBirth: isIdentityWorkEligibilityFolder(selectedEditFolder) ? getStoredDateFromCalendarValue(editDateOfBirth) : "",
        folderId: editDocumentFolderId,
        documentCategory: editDocumentType || getFolderName(editDocumentFolderId),
        effectiveDate: getStoredDateFromCalendarValue(editEffectiveDate),
        expirationDate: getStoredDateFromCalendarValue(editExpirationDate),
      } as BinderDocument);

      const targetFolderId = editDocumentFolderId;
      setShowEditDocumentDialog(false);
      await loadBinder();
      await loadFolderFiles(targetFolderId);
      toast.current?.show({ severity: "success", summary: "Document updated", detail: "Binder document details saved successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update binder document.", life: 4000 });
    } finally {
      setSavingDocumentChanges(false);
    }
  };

  const onDeleteDocument = (binderDocument: BinderDocument) => {
    confirmDialog({
      message: renderConfirmMessage(
        "Delete binder document",
        `Delete "${binderDocument.title || getLatestVersion(binderDocument)?.fileName || "this binder document"}" and remove it from the folder.`,
      ),
      header: "Delete Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          if (!binderDocument.binderDocumentId) {
            return;
          }
          await backendAPIStore.deleteBinderDocument(binderDocument.binderDocumentId);
          await loadBinder();
          if (activeFolderId) {
            await loadFolderFiles(activeFolderId);
          }
          toast.current?.show({ severity: "success", summary: "Deleted", detail: "Binder document deleted.", life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to delete binder document.", life: 4000 });
        }
      },
    });
  };

  const onMarkDocumentAsPrimary = async (binderDocument: BinderDocument) => {
    if (!binderDocument.binderDocumentId || !binder?.binderId) {
      return;
    }

    try {
      await backendAPIStore.updateBinderDocument({
        ...binderDocument,
        binderId: binder.binderId,
        isPrimaryDocument: true,
      } as BinderDocument);
      await loadBinder();
      if (activeFolderId) {
        await loadFolderFiles(activeFolderId);
      }
      toast.current?.show({ severity: "success", summary: "Updated", detail: "Primary binder document updated successfully.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update the primary binder document.", life: 4000 });
    }
  };

  const openShareDialog = () => {
    setShareRecipientEmail("");
    setShareRecipientName("");
    setShareMessage("Please review the documents in this Binder and let me know if anything needs to be updated. Thank you.");
    setShareDialogStep(1);
    setSelectedShareFolderIds(folders.filter((folder) => folder.shareable !== false).map((folder) => folder.folderId || "").filter(Boolean));
    setShareExpirationOption("7");
    setShowNewShareForm(binderShares.length === 0);
    setShowShareDialog(true);
  };

  const getFolderNotes = (folderId?: string) => binderShares.flatMap((share) =>
    (share.folderNotes || []).filter((note) => note.folderId === folderId));

  const openFolderNoteDialog = (folder: BinderFolder) => {
    setSelectedNoteFolder(folder);
    setFolderNoteDraft("");
    setShowFolderNoteDialog(true);
  };

  const onSaveFolderNote = async () => {
    if (!selectedNoteFolder?.folderId || !folderNoteDraft.trim()) {
      return;
    }
    const share = isSharedBinder
      ? sharedBinderShare
      : binderShares.find((item) => !(item.folderIds || []).length || (item.folderIds || []).includes(selectedNoteFolder.folderId!));
    if (!share?.shareId) {
      toast.current?.show({ severity: "warn", summary: "Share not found", detail: "This folder is not part of an active Binder share.", life: 3000 });
      return;
    }
    setSavingFolderNote(true);
    try {
      const updatedShare = await backendAPIStore.updateBinderShare({
        shareId: share.shareId,
        folderNote: { folderId: selectedNoteFolder.folderId, note: folderNoteDraft.trim() },
      } as any);
      if (isSharedBinder) {
        setSharedBinderShare(updatedShare || null);
      } else {
        setBinderShares((current) => current.map((item) => item.shareId === updatedShare?.shareId ? updatedShare : item));
      }
      setFolderNoteDraft("");
      toast.current?.show({ severity: "success", summary: "Note added", detail: "The folder note was saved.", life: 2500 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save the folder note.", life: 3500 });
    } finally {
      setSavingFolderNote(false);
    }
  };

  const shareableFolders = folders.filter((folder) => folder.shareable !== false);

  const openEditShareFoldersDialog = (share: BinderShare) => {
    setEditingShare(share);
    const sharedFolderIds = Array.isArray(share.folderIds) ? share.folderIds.filter(Boolean) : [];
    setSelectedShareFolderIds(sharedFolderIds.length > 0
      ? sharedFolderIds
      : shareableFolders.map((folder) => folder.folderId || "").filter(Boolean));
    setShowEditShareFoldersDialog(true);
  };

  const runComplianceAfterUpload = async () => {
    if (!binder?.binderId) {
      return;
    }
    try {
      const response = await backendAPIStore.runBinderCompliance(binder.binderId);
      setLatestComplianceRun(response || null);
    } catch (error: any) {
      toast.current?.show({ severity: "warn", summary: "Rule check unavailable", detail: error?.message || "The file was uploaded, but the Binder rules could not be checked yet.", life: 4000 });
    }
  };

  const onSaveShareFolders = async () => {
    if (!editingShare?.shareId || !selectedShareFolderIds.length) {
      return;
    }
    setSavingShareFolders(true);
    try {
      const updatedShare = await backendAPIStore.updateBinderShare({
        shareId: editingShare.shareId,
        folderIds: selectedShareFolderIds,
      } as BinderShare);
      setBinderShares((current) => current.map((share) => share.shareId === updatedShare?.shareId ? updatedShare : share));
      setShowEditShareFoldersDialog(false);
      setEditingShare(null);
      toast.current?.show({ severity: "success", summary: "Folders updated", detail: "The Binder share folders were updated.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to update shared folders.", life: 4000 });
    } finally {
      setSavingShareFolders(false);
    }
  };
  const shareExpirationOptions = [
    { label: "1 day", value: "1" },
    { label: "7 days", value: "7" },
    { label: "14 days", value: "14" },
    { label: "30 days", value: "30" },
    { label: "Forever", value: "forever" },
  ];

  const getShareExpirationLabel = () => shareExpirationOptions.find((option) => option.value === shareExpirationOption)?.label || "7 days";

  const getShareExpirationDate = () => {
    if (shareExpirationOption === "forever") {
      return "No expiration";
    }
    const days = Number(shareExpirationOption);
    return formatDateTime(new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString());
  };

  const reviewShare = () => {
    if (!shareRecipientEmail.trim()) {
      toast.current?.show({ severity: "warn", summary: "Email required", detail: "Enter the provider email before continuing.", life: 3000 });
      return;
    }
    if (!selectedShareFolderIds.length) {
      toast.current?.show({ severity: "warn", summary: "Folder required", detail: "Select at least one shareable folder.", life: 3000 });
      return;
    }
    setShareDialogStep(2);
  };

  const onShareBinder = async () => {
    if (!binder?.binderId) {
      return;
    }

    if (!shareRecipientEmail.trim()) {
      toast.current?.show({ severity: "warn", summary: "Email required", detail: "Enter the provider email before sending.", life: 3000 });
      return;
    }

    setSharingBinder(true);
    try {
      await backendAPIStore.createBinderShare({
        binderId: binder.binderId,
        shareMethod: "email",
        folderIds: selectedShareFolderIds,
        expirationOption: shareExpirationOption,
        recipientEmail: shareRecipientEmail.trim().toLowerCase(),
        recipientName: shareRecipientName.trim(),
        message: shareMessage.trim(),
      } as BinderShare);
      setShowShareDialog(false);
      if (binder.binderId) {
        const shares = await backendAPIStore.listBinderSharesByBinderId(binder.binderId);
        setBinderShares(shares || []);
      }
      toast.current?.show({
        severity: "success",
        summary: "Binder sent",
        detail: "The provider email was sent successfully.",
        life: 3000,
      });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to share the binder.", life: 4000 });
    } finally {
      setSharingBinder(false);
    }
  };

  const onUnshareBinder = (share: BinderShare) => {
    if (!share.shareId) {
      return;
    }

    confirmDialog({
      message: renderConfirmMessage(
        "Unshare binder",
        `Remove access for ${share.recipientEmail || share.recipientName || "this provider"}?`,
      ),
      header: "Unshare Confirmation",
      icon: "pi pi-exclamation-triangle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.deleteBinderShare(share.shareId!);
          const shares = await backendAPIStore.listBinderSharesByBinderId(binder?.binderId || "");
          setBinderShares(shares || []);
          toast.current?.show({ severity: "success", summary: "Binder unshared", detail: "The provider no longer has access to this binder.", life: 3000 });
        } catch (error: any) {
          toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to unshare the binder.", life: 4000 });
        }
      },
    });
  };

  const onUpdateSharedReview = async (reviewStatus?: "approved" | "needs_update" | "rejected") => {
    if (!sharedBinderShare?.shareId) {
      return;
    }

    setSavingSharedReview(true);
    try {
      const updatedShare = await backendAPIStore.updateBinderShare({
        shareId: sharedBinderShare.shareId,
        reviewStatus,
        reviewNotes: reviewNotes.trim(),
      } as BinderShare);
      setSharedBinderShare(updatedShare || null);
      setReviewNotes("");
      toast.current?.show({ severity: "success", summary: "Review saved", detail: "The Binder owner will receive your update by email.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save the review.", life: 4000 });
    } finally {
      setSavingSharedReview(false);
    }
  };

  const onSendJobQuestions = async () => {
    if (!sharedBinderShare?.shareId) {
      return;
    }

    if (!providerJobQuestions.trim()) {
      toast.current?.show({ severity: "warn", summary: "Job questions required", detail: "Enter the job questions before sending them.", life: 3000 });
      return;
    }

    setSavingJobQuestions(true);
    try {
      const updatedShare = await backendAPIStore.updateBinderShare({
        shareId: sharedBinderShare.shareId,
        jobQuestions: providerJobQuestions.trim(),
      } as BinderShare);
      setSharedBinderShare(updatedShare || null);
      setProviderJobQuestions(String(updatedShare?.jobQuestions || providerJobQuestions.trim()));
      toast.current?.show({ severity: "success", summary: "Job questions sent", detail: "The Binder owner will receive an email and can answer from their Binder.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to send the job questions.", life: 4000 });
    } finally {
      setSavingJobQuestions(false);
    }
  };

  const getSelectedJobQuestionTemplate = () => savedJobQuestionTemplates.find((template) => String(template?.templateId || "").trim() === selectedJobQuestionTemplateId) || null;

  const onApplySavedJobQuestionTemplate = () => {
    const selectedTemplate = getSelectedJobQuestionTemplate();
    if (!selectedTemplate?.questions) {
      toast.current?.show({ severity: "warn", summary: "Template required", detail: "Choose a saved template first.", life: 3000 });
      return;
    }
    setProviderJobQuestions(String(selectedTemplate.questions || ""));
  };

  const onSaveCurrentJobQuestionsAsTemplate = async () => {
    if (!jobQuestionTemplateName.trim()) {
      toast.current?.show({ severity: "warn", summary: "Template name required", detail: "Enter a name for this question set.", life: 3000 });
      return;
    }

    if (!providerJobQuestions.trim()) {
      toast.current?.show({ severity: "warn", summary: "Questions required", detail: "Enter job questions before saving a template.", life: 3000 });
      return;
    }

    setSavingJobQuestionTemplate(true);
    try {
      const nextTemplates = [
        {
          templateId: uuidv4(),
          name: jobQuestionTemplateName.trim(),
          questions: providerJobQuestions.trim(),
          updatedAt: new Date().toISOString(),
        },
        ...savedJobQuestionTemplates.filter((template) => String(template?.name || "").trim().toLowerCase() !== jobQuestionTemplateName.trim().toLowerCase()),
      ].slice(0, 20);
      const response = await backendAPIStore.updateCurrentUserPreferences({
        binderJobQuestionTemplates: nextTemplates,
      });
      const savedTemplates = Array.isArray(response?.binderJobQuestionTemplates) ? response.binderJobQuestionTemplates : nextTemplates;
      setSavedJobQuestionTemplates(savedTemplates);
      setSelectedJobQuestionTemplateId(String(savedTemplates?.[0]?.templateId || ""));
      setShowSaveJobQuestionTemplateDialog(false);
      setJobQuestionTemplateName("");
      toast.current?.show({ severity: "success", summary: "Template saved", detail: "You can now reuse this question set on other caregiver applications.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save the question template.", life: 4000 });
    } finally {
      setSavingJobQuestionTemplate(false);
    }
  };

  const onDeleteSavedJobQuestionTemplate = async () => {
    const selectedTemplate = getSelectedJobQuestionTemplate();
    if (!selectedTemplate) {
      toast.current?.show({ severity: "warn", summary: "Template required", detail: "Choose a saved template first.", life: 3000 });
      return;
    }

    setSavingJobQuestionTemplate(true);
    try {
      const nextTemplates = savedJobQuestionTemplates.filter((template) => String(template?.templateId || "").trim() !== String(selectedTemplate.templateId || "").trim());
      const response = await backendAPIStore.updateCurrentUserPreferences({
        binderJobQuestionTemplates: nextTemplates,
      });
      const savedTemplates = Array.isArray(response?.binderJobQuestionTemplates) ? response.binderJobQuestionTemplates : nextTemplates;
      setSavedJobQuestionTemplates(savedTemplates);
      setSelectedJobQuestionTemplateId("");
      toast.current?.show({ severity: "success", summary: "Template removed", detail: "The saved question template was deleted.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to remove the question template.", life: 4000 });
    } finally {
      setSavingJobQuestionTemplate(false);
    }
  };

  const openAnswerQuestionsDialog = (share: BinderShare) => {
    setSelectedQuestionsShare(share);
    setJobQuestionAnswersDraft(String(share.jobQuestionAnswers || ""));
    setShowAnswerQuestionsDialog(true);
  };

  const openShareHistoryDialog = (share: BinderShare) => {
    setSelectedHistoryShare(share);
    setShowShareHistoryDialog(true);
  };

  const onSubmitJobQuestionAnswers = async () => {
    if (!selectedQuestionsShare?.shareId) {
      return;
    }

    if (!jobQuestionAnswersDraft.trim()) {
      toast.current?.show({ severity: "warn", summary: "Answer required", detail: "Enter your answer before sending it back to the provider.", life: 3000 });
      return;
    }

    setSavingJobQuestionAnswers(true);
    try {
      await backendAPIStore.updateBinderShare({
        shareId: selectedQuestionsShare.shareId,
        jobQuestionAnswers: jobQuestionAnswersDraft.trim(),
      } as BinderShare);
      setShowAnswerQuestionsDialog(false);
      setSelectedQuestionsShare(null);
      setJobQuestionAnswersDraft("");
      if (binder?.binderId) {
        const shares = await backendAPIStore.listBinderSharesByBinderId(binder.binderId);
        setBinderShares(shares || []);
      }
      toast.current?.show({ severity: "success", summary: "Answers sent", detail: "The provider will receive your answers by email.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to send your answers.", life: 4000 });
    } finally {
      setSavingJobQuestionAnswers(false);
    }
  };

  const openReviewDocumentDialog = (binderDocument: BinderDocument) => {
    setSelectedReviewDocument(binderDocument);
    setReviewDocumentStatus(String(binderDocument.reviewStatus || ""));
    setReviewDocumentNote(String(binderDocument.reviewNote || ""));
    setShowReviewDocumentDialog(true);
  };

  const onSaveReviewDocument = async () => {
    if (!selectedReviewDocument?.binderDocumentId) {
      return;
    }

    setSavingReviewDocument(true);
    try {
      const updatedDocument = await backendAPIStore.updateBinderDocument({
        binderDocumentId: selectedReviewDocument.binderDocumentId,
        reviewStatus: reviewDocumentStatus || "",
        reviewNote: reviewDocumentNote.trim(),
        reviewUpdatedAt: new Date().toISOString(),
        reviewUpdatedBy: authStore.getUserName() || authStore.getUserEmailId() || "Provider",
      } as BinderDocument);
      setShowReviewDocumentDialog(false);
      setSelectedReviewDocument(null);
      if (activeFolderId) {
        await loadFolderFiles(activeFolderId);
      } else if (binder?.binderId) {
        await loadBinder();
      }
      toast.current?.show({ severity: "success", summary: "File review saved", detail: "The Binder owner can now see your note for this file.", life: 3000 });
    } catch (error: any) {
      toast.current?.show({ severity: "error", summary: getErrorTitle(error), detail: error?.message || "Unable to save the file review.", life: 4000 });
    } finally {
      setSavingReviewDocument(false);
    }
  };

  const renderFolderName = (rowData: BinderFolder) => {
    const folderDocuments = binderDocuments.filter((document) => document.folderId === rowData.folderId);
    const folderNotes = getFolderNotes(rowData.folderId);
    const expiredFolderDocuments = folderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expired");
    const expiringSoonFolderDocuments = folderDocuments.filter((document) => getExpiryStatus(document.expirationDate).label === "Expiring soon");

    return (
      <div className="flex flex-column gap-1">
        <div className="flex align-items-center gap-1" style={{ minWidth: 0 }}>
          <span style={{ flex: "1 1 auto", minWidth: 0, fontWeight: 700, color: "#0f172a", overflowWrap: "anywhere" }}>
            {rowData.folderName || "Folder"}
            {folderDocuments.length > 0 ? (
              <span style={{ color: "#64748b", whiteSpace: "nowrap" }}>{` (${folderDocuments.length})`}</span>
            ) : null}
            {rowData.requiredForCompliance ? <span className="binder-required-folder" aria-label="Required folder" data-pr-tooltip="Required folder" style={{ color: "#dc2626", whiteSpace: "nowrap", cursor: "help" }}> *</span> : null}
          </span>
          {!isSharedBinder && rowData.shareable === false ? (
            <i
              className="binder-private-folder-lock pi pi-lock"
              aria-label="Private folder"
              data-pr-tooltip="Private folder — not shared"
              style={{ flex: "0 0 auto", color: "#64748b", fontSize: "0.8rem" }}
            />
          ) : null}
          {false && (
            <Button
              type="button"
              icon="pi pi-info-circle"
              className="p-button-text p-button-rounded p-button-sm"
              style={{ flex: "0 0 auto", padding: "0.15rem" }}
              tooltip={[
                rowData.guidanceDescription || "",
                Array.isArray(rowData.suggestedDocuments) && rowData.suggestedDocuments?.length
                  ? `Suggested documents: ${rowData.suggestedDocuments?.join(", ")}`
                  : "",
              ].filter(Boolean).join("\n\n")}
              tooltipOptions={{ position: "top" }}
              aria-label={`Guide for ${rowData.folderName || "folder"}`}
            />
          )}
          {!isSharedBinder ? (
            <Button
              type="button"
              icon="pi pi-pencil"
              className="p-button-text p-button-rounded p-button-sm"
              style={{ flex: "0 0 auto", width: "1.5rem", height: "1.5rem", minWidth: 0, minHeight: 0, padding: "0.15rem", lineHeight: 1 }}
              tooltip="Edit folder properties"
              tooltipOptions={{ position: "top" }}
              aria-label={`Edit ${rowData.folderName || "folder"}`}
              onClick={() => openEditFolderDialog(rowData)}
            />
          ) : null}
          {!isSharedBinder && rowData.folderType === "custom" ? (
            <Button
              type="button"
              icon="pi pi-trash"
              severity="danger"
              className="p-button-text p-button-rounded p-button-sm"
              style={{ flex: "0 0 auto", width: "1.5rem", height: "1.5rem", minWidth: 0, minHeight: 0, padding: "0.15rem", lineHeight: 1 }}
              tooltip="Delete custom folder"
              tooltipOptions={{ position: "top" }}
              aria-label={`Delete ${rowData.folderName || "custom folder"}`}
              onClick={() => onDeleteCustomFolder(rowData)}
            />
          ) : null}
          {false && (folderNotes.length > 0 || isSharedBinder) && (
            <Button
              type="button"
              label={folderNotes.length > 0 ? `Notes (${folderNotes.length})` : "Add note"}
              icon="pi pi-comment"
              severity="warning"
              className="p-button-sm"
              style={{ flex: "0 0 auto", padding: "0.25rem 0.5rem" }}
              tooltip="Add any note here and it will be visible to the Binder owner."
              tooltipOptions={{ position: "top" }}
              onClick={() => openFolderNoteDialog(rowData)}
            />
          )}
        </div>
        {false && rowData.guidanceCitation ? (
          rowData.guidanceSourceUrl ? (
            <a
              href={String(rowData.guidanceSourceUrl || "").trim()}
              target="_blank"
              rel="noreferrer"
              style={{ alignSelf: "flex-start", display: "inline-block", width: "fit-content", color: "#b45309", fontSize: "0.75rem", fontWeight: 600, textDecoration: "none" }}
            >
              {rowData.guidanceCitation}
            </a>
          ) : (
            <span style={{ color: "#b45309", fontSize: "0.75rem", fontWeight: 600 }}>{rowData.guidanceCitation}</span>
          )
        ) : null}
        {expiredFolderDocuments.length > 0 || expiringSoonFolderDocuments.length > 0 ? (
          <div className="flex flex-wrap gap-1">
            {expiredFolderDocuments.length > 0 ? <Tag value={`${expiredFolderDocuments.length} expired`} severity="danger" rounded /> : null}
            {expiringSoonFolderDocuments.length > 0 ? <Tag value={`${expiringSoonFolderDocuments.length} expiring soon`} severity="warning" rounded /> : null}
          </div>
        ) : null}
      </div>
    );
  };

  const renderFolderGuidance = (rowData: BinderFolder) => (
    <div className="flex flex-column gap-2">
      <div className="flex align-items-center justify-content-between gap-2">
        <strong style={{ color: "#334155", fontSize: "0.82rem" }}>Rule: {rowData.guidanceTitle || "Binder requirement"}</strong>
        <div className="flex align-items-center gap-1">
          {rowData.requiredForCompliance ? <Tag value="Required" severity="warning" rounded /> : <Tag value="Optional" severity="info" rounded />}
          {renderFolderComplianceStatus(rowData)}
        </div>
      </div>
      <span style={appMutedTextStyle}>{rowData.guidanceDescription || "No guidance linked for this folder yet."}</span>
      {Array.isArray(rowData.suggestedDocuments) && rowData.suggestedDocuments.length ? (
        <div style={{ color: "#475569", fontSize: "0.88rem", lineHeight: 1.6 }}>
          <strong>Suggested documents:</strong> {rowData.suggestedDocuments.join(", ")}
        </div>
      ) : null}
      {rowData.guidanceSourceUrl ? (
        <Button
          type="button"
          label={rowData.guidanceCitation || "Open Guidance"}
          icon="pi pi-external-link"
          className="p-button-text p-button-sm"
          style={{ alignSelf: "flex-start", width: "fit-content", padding: "0.2rem 0.35rem", color: "#b45309", fontWeight: 700, textDecoration: "underline", textUnderlineOffset: "2px" }}
          aria-label={`Open ${rowData.guidanceCitation || "guidance"}`}
          onClick={() => openUrlInNewTab(String(rowData.guidanceSourceUrl || "").trim())}
        />
      ) : null}
    </div>
  );

  const renderDocumentEffectiveDate = (rowData: BinderDocument) => formatStoredDate(rowData.effectiveDate);

  const renderDocumentExpirationDate = (rowData: BinderDocument) => String(rowData.expirationDate || "").trim() ? formatStoredDate(rowData.expirationDate) : "";

  const renderDocumentExpiryStatus = (rowData: BinderDocument) => {
    const status = getExpiryStatus(rowData.expirationDate);
    return <Tag value={status.label} severity={status.severity} />;
  };

  const renderDocumentReviewStatus = (rowData: BinderDocument) => {
    const normalizedStatus = String(rowData.reviewStatus || "").trim().toLowerCase();
    if (normalizedStatus === "looks_good") {
      return <Tag value="Looks Good" severity="success" />;
    }
    if (normalizedStatus === "needs_update") {
      return <Tag value="Need Updates" severity="warning" />;
    }
    return <Tag value="Not Reviewed" severity="info" />;
  };

  const renderDocumentReviewNote = (rowData: BinderDocument) => rowData.reviewNote || "No file note yet";

  const documentsNeedingUpdates = activeFolderDocuments.filter((document) => isDocumentMarkedForUpdate(document));

  const renderDocumentRequirement = (rowData: BinderDocument) => {
    const folder = folders.find((item) => item.folderId === rowData.folderId);
    return folder?.requiredForCompliance ? <Tag value="Required" severity="warning" /> : <Tag value="Optional" severity="info" />;
  };

  const renderFolderComplianceStatus = (rowData: BinderFolder) => {
    const finding = getFolderComplianceFinding(rowData);
    if (!finding) {
      return rowData.requiredForCompliance ? <Tag value="Not checked" severity="info" /> : <Tag value="Optional" severity="info" />;
    }

    return <Tag value={getFindingLabel(finding.status)} severity={getFindingSeverity(finding.status)} />;
  };

  const renderFindingStatus = (rowData: any) => (
    <Tag value={getFindingLabel(rowData.status)} severity={getFindingSeverity(rowData.status)} />
  );

  const renderFindingLatestDocument = (rowData: any) => {
    if (!rowData.latestDocumentTitle) {
      return <span style={appMutedTextStyle}>No current document</span>;
    }

    return (
      <div className="flex flex-column gap-1">
        <span>{rowData.latestDocumentTitle}</span>
        <span style={appMutedTextStyle}>
          {rowData.latestVersionNumber ? `v${rowData.latestVersionNumber}` : "No version"}{rowData.lastUpdatedAt ? ` • ${formatDateTime(rowData.lastUpdatedAt)}` : ""}
        </span>
      </div>
    );
  };

  const renderVersions = (rowData: BinderDocument) => {
    const versions = documentVersionsById[rowData.binderDocumentId || ""] || [];
    if (versions.length === 0) {
      if (rowData.latestVersionNumber) {
        return <Tag value={`v${rowData.latestVersionNumber}`} severity="success" />;
      }
      return <span style={appMutedTextStyle}>No versions yet</span>;
    }

    return (
      <div className="flex flex-wrap gap-1">
        {versions.map((version) => (
          <Tag
            key={version.versionId}
            value={`v${version.versionNumber}${version.fileName ? ` - ${version.fileName}` : ""}`}
            severity={version.versionNumber === rowData.latestVersionNumber ? "success" : "info"}
          />
        ))}
      </div>
    );
  };

  const renderDocumentTitle = (rowData: BinderDocument) => {
    const latestVersion = getLatestVersion(rowData);
    return (
      <div className="flex flex-wrap align-items-center gap-2">
        <span>{latestVersion?.fileName || rowData.title || "Untitled document"}</span>
        {rowData.isPrimaryDocument ? <Tag value="Primary" severity="warning" /> : null}
      </div>
    );
  };

  const renderLastUpdated = (rowData: BinderDocument) => {
    const latestVersion = getLatestVersion(rowData);
    return <span>{formatDateTime(latestVersion?.uploadedAt || rowData.createdAt)}</span>;
  };

  const renderUploadedBy = (rowData: BinderDocument) => {
    const latestVersion = getLatestVersion(rowData);
    return <span>{latestVersion?.uploadedBy || rowData.createdBy || "Unknown"}</span>;
  };

  const renderDocumentAnalysisStatus = (rowData: BinderDocument) => {
    const runningAnalysis = runningAnalysisByDocumentId[rowData.binderDocumentId || ""];
    if (runningAnalysis) {
      return <Tag value="Running" severity="warning" />;
    }
    return <Tag value={getAnalysisLabel(rowData.latestAnalysisStatus)} severity={getAnalysisSeverity(rowData.latestAnalysisStatus)} />;
  };

  const renderDocumentActions = (rowData: BinderDocument) => (
    <div className="flex flex-wrap gap-2">
      {!isSharedBinder && isCaregiverJobBinder && isDocumentMarkedForUpdate(rowData) && (
        <Button
          label="Upload Update"
          icon="pi pi-upload"
          severity="warning"
          className="p-button-sm"
          rounded
          onClick={() => openVersionDialog(rowData)}
        />
      )}
      <Button
        label="Preview"
        icon="pi pi-eye"
        className="p-button-outlined p-button-sm"
        rounded
        onClick={() => {
          void (async () => {
            const latestVersion = await getLatestVersionForAction(rowData);
            if (latestVersion) {
              await onPreviewVersion(rowData, latestVersion);
            } else {
              toast.current?.show({ severity: "warn", summary: "Preview unavailable", detail: "No latest version is available for preview.", life: 3000 });
            }
          })();
        }}
      />
      <Button
        label="Download"
        icon="pi pi-download"
        rounded
        className="p-button-outlined p-button-sm"
        onClick={() => {
          void onOpenLatestVersion(rowData);
        }}
      />
      {!isSharedBinder && <ActionMenuButton
        label="More"
        rounded
        className="p-button-outlined p-button-sm"
        model={[
          ...(isSharedBinder ? [] : ENABLE_BINDER_AI_REVIEW_UI ? [
            {
              label: "Analyze Latest",
              icon: "pi pi-sparkles",
              command: () => {
                void onRunDocumentAnalysis(rowData);
              },
            },
            {
              label: "View Analysis",
              icon: "pi pi-search",
              disabled: !rowData?.binderDocumentId,
              command: () => {
                void openAnalysisDialog(rowData);
              },
            },
          ] : []),
          ...(canUseEfax ? [{
            label: "Send Fax",
            icon: "pi pi-send",
            command: () => {
              void openSendFaxDialog(rowData);
            },
          }] : []),
          {
            label: rowData.folderId === folders.find((folder) => folder.folderCode === "assessment_preliminary_service_plan")?.folderId
              ? "Mark Current Assessment"
              : "Mark Primary",
            icon: "pi pi-star",
            command: () => {
              void onMarkDocumentAsPrimary(rowData);
            },
          },
          {
            label: "Edit",
            icon: "pi pi-pencil",
            command: () => openEditDocumentDialog(rowData),
          },
          {
            label: "Upload Version",
            icon: "pi pi-upload",
            command: () => openVersionDialog(rowData),
          },
          {
            label: "View Versions",
            icon: "pi pi-history",
            command: () => {
              void openVersionHistoryDialog(rowData);
            },
          },
          {
            label: "Delete",
            icon: "pi pi-trash",
            command: () => onDeleteDocument(rowData),
          },
        ]}
      />}
    </div>
  );

  if (loading) {
    return (
      <div className="m-3 flex justify-content-center">
        <ProgressSpinner />
      </div>
    );
  }

  if (isSharedResidentBinder && sharedResidentBinderAccessDenied) {
    return (
      <div className="m-3" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
        <Toast ref={toast} />
        <Card style={appSectionCardStyle}>
          <div className="flex flex-column gap-3">
            {renderSurfaceHeader("Resident Binder", "Binder access is not enabled for this shared resident.", "pi pi-lock")}
            <div style={appNeutralPanelStyle}>
              Ask the provider to enable Binder access for this resident in Manage Staff before opening this page.
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                label="Back to Profile"
                icon="pi pi-arrow-left"
                severity="warning"
                className="p-button-outlined"
                onClick={() => navigate(documentsPagePath)}
              />
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="m-3" style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      <Toast ref={toast} />
      <Dialog
        visible={showStaffCredentialsDialog}
        modal
        header={renderSurfaceHeader("Staff Credentials", "Keep your delegation credentials and training verification current.", "pi pi-id-card")}
        style={{ width: "54rem", maxWidth: "95vw" }}
        onHide={() => !savingStaffCredentials && setShowStaffCredentialsDialog(false)}
        footer={<div className="flex justify-content-between w-full"><Button type="button" label="Cancel" severity="secondary" outlined rounded disabled={savingStaffCredentials} onClick={() => setShowStaffCredentialsDialog(false)} /><Button type="button" label="Save Credentials" icon="pi pi-save" severity="warning" rounded loading={savingStaffCredentials} onClick={() => void saveStaffCredentials()} /></div>}
      >
        <StaffDelegationRequirementsForm value={staffCredentials} onChange={setStaffCredentials} showValidationErrors={showCredentialValidationErrors} name={staffCredentialsName} email={staffCredentialsEmail} />
      </Dialog>
      <Tooltip target=".binder-private-folder-lock" position="top" />
      <Tooltip key={folderTooltipRefreshKey} target=".binder-required-folder" content="Required folder" position="top" />

      <Dialog
        visible={showComplianceResults && !!latestComplianceRun}
        modal
        header={renderSurfaceHeader("Binder Rule Results", "Non-compliant folders are shown first.", "pi pi-check-square")}
        style={{ width: "58rem", maxWidth: "95vw" }}
        onHide={() => setShowComplianceResults(false)}
      >
        <div className="flex flex-wrap gap-2 mb-3">
          <Tag value={`${latestComplianceRun?.compliantCount || 0} compliant`} severity="success" rounded />
          <Tag value={`${latestComplianceRun?.missingCount || 0} missing`} severity="danger" rounded />
          <Tag value={`${latestComplianceRun?.outdatedCount || 0} outdated`} severity="warning" rounded />
          <Tag value={`${latestComplianceRun?.reviewNeededCount || 0} review needed`} severity="info" rounded />
        </div>
        <DataTable value={complianceDialogFindings} size="small" stripedRows emptyMessage="No rule results found.">
          <Column header="Folder" body={(finding: any) => finding.folderName || "Folder"} />
          <Column header="Status" body={(finding: any) => <Tag value={getFindingLabel(finding.status)} severity={getFindingSeverity(finding.status)} rounded />} />
          <Column header="Summary" body={(finding: any) => finding.summary || finding.details || "No additional details."} />
          <Column header="Files" body={(finding: any) => String(finding.documentCount || 0)} />
        </DataTable>
      </Dialog>

      <Dialog
        visible={showBinderConfigDialog}
        modal
        header={renderSurfaceHeader("Update Binder Config", "Create a new Binder using the selected facility rules.", "pi pi-cog")}
        style={appDialogStyle}
        headerStyle={appDialogHeaderStyle}
        contentStyle={appDialogContentStyle}
        onHide={() => !resettingBinderConfig && setShowBinderConfigDialog(false)}
      >
        <div className="flex flex-column gap-3">
          <div className="p-3 border-round" style={{ background: "#fff7ed", border: "1px solid #fed7aa" }}>
            <strong>Existing Binder will be archived.</strong>
            <div className="mt-1" style={appMutedTextStyle}>Your files and shares remain with the archived Binder. The new Binder starts with the facility-specific folders and rules.</div>
          </div>
          <div>
            <label htmlFor="resetBinderFacilityType" className="block mb-2"><strong>{authExperience.providerHome.facilityTypeLabel}</strong></label>
            <Dropdown
              id="resetBinderFacilityType"
              value={resetFacilityType}
              options={facilityTypeOptions}
              optionLabel="label"
              optionValue="value"
              onChange={(event) => setResetFacilityType(event.value)}
              className="w-full"
            />
          </div>
          <div className="flex justify-content-end gap-2">
            <Button label="Cancel" className="p-button-outlined" onClick={() => setShowBinderConfigDialog(false)} disabled={resettingBinderConfig} />
            <Button label={resettingBinderConfig ? "Creating..." : "Create New Binder"} icon="pi pi-refresh" severity="warning" onClick={() => void onResetBinderConfig()} disabled={resettingBinderConfig || !resetFacilityType} />
          </div>
        </div>
      </Dialog>

      {!binder && !isSharedBinder ? (
        <Card style={appSectionCardStyle}>
          <div className="flex flex-column gap-3">
            {renderSurfaceHeader(resolvedPageTitle, "", "pi pi-folder-open")}
            {isCaregiverJobBinder ? (
              <div className="flex flex-column align-items-center text-center" style={{ width: "100%", maxWidth: "28rem", margin: "0 auto" }}>
                <label htmlFor="binderFacilityType" className="block mb-2"><strong>{authExperience.providerHome.facilityTypeLabel}</strong></label>
                <Dropdown
                  id="binderFacilityType"
                  value={selectedFacilityType}
                  options={facilityTypeOptions}
                  optionLabel="label"
                  optionValue="value"
                  onChange={(event) => setSelectedFacilityType(event.value)}
                  placeholder="Select facility type"
                  className="w-full"
                />
                <small className="block mt-2" style={appMutedTextStyle}>This determines which checklist rules apply to this Binder.</small>
              </div>
            ) : null}
            <Button
              label={bootstrapping ? "Creating Binder..." : "Create Binder"}
              icon="pi pi-plus"
              severity="warning"
              rounded
              className="align-self-center"
              disabled={bootstrapping}
              onClick={() => void onBootstrapBinder()}
            />
          </div>
        </Card>
      ) : null}

      {false && isSharedBinder && sharedBinderShare && (
        <Card style={appSectionCardStyle}>
          <div className="flex flex-column gap-3">
            <div className="grid align-items-stretch">
              <div className={sharedBinderShare?.message ? "col-12 lg:col-6" : "col-12"}>
                {renderSurfaceHeader("Review Binder", "Review the Binder documents.", "pi pi-send")}
              </div>
              {sharedBinderShare?.message && (
                <div>
                  <div style={{ fontWeight: 700, color: "#0f172a" }}>Message from the User</div>
                  <div className="mt-2" style={appMutedTextStyle}>{sharedBinderShare?.message}</div>
                </div>
              )}
            </div>
            {sharedBinderShare?.jobQuestionAnswers && (
              <div style={appNeutralPanelStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>Binder answers</div>
                <div className="mt-2" style={appMutedTextStyle}>{sharedBinderShare?.jobQuestionAnswers}</div>
              </div>
            )}
            {false && !!sharedBinderShare?.history?.length && (
              <div style={appNeutralPanelStyle}>
                <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Activity History</div>
                <div className="flex flex-column gap-3">
                  {sharedBinderShare?.history?.map((historyItem: any, index: number) => (
                    <div key={`${historyItem.eventType || "event"}-${historyItem.createdAt || index}`} style={{ borderLeft: "3px solid #f59e0b", paddingLeft: "0.85rem" }}>
                      <div style={{ fontWeight: 700, color: "#0f172a" }}>{historyItem.title || "Update"}</div>
                      <div style={appMutedTextStyle}>{historyItem.description || ""}</div>
                      <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.2rem" }}>
                        {historyItem.actorLabel || "System"}{historyItem.createdAt ? ` • ${formatDateTime(historyItem.createdAt)}` : ""}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {false && <div style={appFormSectionStyle}>
              <label htmlFor="sharedBinderReviewNotes" className="mb-2 block"><strong>Review Notes</strong></label>
              <InputTextarea
                id="sharedBinderReviewNotes"
                value={reviewNotes}
                onChange={(event) => setReviewNotes(event.target.value)}
                rows={4}
                autoResize
                placeholder="Example: Please upload your most recent CPR card."
                className="w-full"
              />
            </div>}
            {false && <div className="flex flex-wrap gap-2">
              <Button label="Accept" icon="pi pi-check" severity="success" onClick={() => void onUpdateSharedReview("approved")} disabled={savingSharedReview} />
              <Button label="Need Updates" icon="pi pi-refresh" severity="warning" onClick={() => void onUpdateSharedReview("needs_update")} disabled={savingSharedReview} />
              <Button label="Reject" icon="pi pi-times" severity="danger" onClick={() => void onUpdateSharedReview("rejected")} disabled={savingSharedReview} />
            </div>}
          </div>
        </Card>
      )}

      {binder && (
        <>
          {ENABLE_BINDER_AI_REVIEW_UI && (
            <div ref={complianceResultsSectionRef}>
              <Card style={appSectionCardStyle}>
                <div className="flex flex-wrap justify-content-between align-items-start gap-3 mb-3">
                  <div className="flex flex-column gap-2">
                    {renderSurfaceHeader(
                      "Compliance Results",
                      latestComplianceRun ? `Latest check recorded ${formatDateTime(latestComplianceRun.createdAt)}.` : "Run a binder check to evaluate required folders and latest documents.",
                      "pi pi-shield",
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Tag value={latestComplianceRun?.status || "Not run yet"} severity={complianceSeverity} />
                      {latestComplianceRun && (
                        <>
                          <Tag value={`${latestComplianceRun.compliantCount || 0} compliant`} severity="success" />
                          <Tag value={`${latestComplianceRun.missingCount || 0} missing`} severity="danger" />
                          <Tag value={`${latestComplianceRun.outdatedCount || 0} outdated`} severity="warning" />
                          <Tag value={`${latestComplianceRun.reviewNeededCount || 0} review`} severity="info" />
                        </>
                      )}
                    </div>
                    <div style={appMutedTextStyle}>
                      {latestComplianceRun?.summary || "No compliance results yet."}
                    </div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.92rem" }}>
                      {latestComplianceRun?.ruleSetVersion
                        ? `Rule set ${latestComplianceRun.ruleSetVersion}`
                        : "Rule set will appear after the first check."}
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    <Button
                      label={runningCompliance ? "Checking..." : "Run Check"}
                      icon="pi pi-shield"
                      severity="warning"
                      rounded
                      disabled={runningCompliance}
                      onClick={() => void onRunCompliance()}
                    />
                    {latestComplianceRun && (
                      <Button
                        label={showComplianceResults ? "Hide Results" : "View Results"}
                        icon={showComplianceResults ? "pi pi-eye-slash" : "pi pi-list"}
                        severity="secondary"
                        text
                        onClick={() => {
                          const nextValue = !showComplianceResults;
                          setShowComplianceResults(nextValue);
                          if (nextValue) {
                            window.setTimeout(() => {
                              complianceResultsSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                            }, 50);
                          }
                        }}
                      />
                    )}
                  </div>
                </div>

                {!latestComplianceRun ? (
                  <div style={appAccentPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#9a3412" }}>No compliance results yet</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Run the binder check to review required folders, uploaded records, and latest versions before inspection.
                    </div>
                  </div>
                ) : !showComplianceResults ? (
                  <div style={appNeutralPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Compliance results hidden</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Use <strong>View Results</strong> from the binder check panel to open the detailed findings table when you need it.
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-column gap-3">
                    <div className="flex flex-wrap gap-2">
                      <Tag value={`${latestComplianceRun.totalRuleCount || 0} required checks`} severity="info" />
                      <Tag value={`${latestComplianceRun.compliantCount || 0} compliant`} severity="success" />
                      <Tag value={`${latestComplianceRun.missingCount || 0} missing`} severity="danger" />
                      <Tag value={`${latestComplianceRun.outdatedCount || 0} outdated`} severity="warning" />
                      <Tag value={`${latestComplianceRun.reviewNeededCount || 0} review needed`} severity="info" />
                    </div>
                    <div style={appDataTableWrapperStyle}>
                      <DataTable value={complianceFindings} paginator rows={10} emptyMessage="No compliance findings available yet.">
                        <Column field="folderName" header="Folder" />
                        <Column header="Status" body={renderFindingStatus} />
                        <Column field="requirement" header="Requirement" />
                        <Column header="Latest Document" body={renderFindingLatestDocument} />
                        <Column field="aiSummary" header="AI Review" body={renderComplianceAiReview} />
                        <Column field="summary" header="Summary" />
                        <Column field="details" header="Details" />
                      </DataTable>
                    </div>
                  </div>
                )}
              </Card>
            </div>
          )}

          {ENABLE_BINDER_AI_REVIEW_UI && (
            <Card style={appSectionCardStyle}>
              <div className="flex flex-column gap-3">
                <div className="flex flex-wrap justify-content-between align-items-start gap-3">
                  <div className="flex flex-column gap-2">
                    {renderSurfaceHeader(
                      "AI Review Watchlist",
                      binderLatestAnalyses.length > 0
                        ? "Binder-wide AI review attention items from the latest analyzed version of each file."
                        : "Run Analyze Latest on binder files to build this watchlist.",
                      "pi pi-sparkles",
                    )}
                    <div className="flex flex-wrap gap-2">
                      <Tag value={`${binderLatestAnalyses.length} analyzed`} severity="info" />
                      <Tag value={`${runningAnalysisCount} running`} severity="warning" />
                      <Tag value={`${failedAnalysisCount} failed`} severity="danger" />
                      <Tag value={`${staleAnalysisCount} stale`} severity="warning" />
                      <Tag value={`${lowConfidenceAnalysisCount} low confidence`} severity="info" />
                      <Tag value={`${binderAttentionAnalyses.length} need attention`} severity={binderAttentionAnalyses.length > 0 ? "danger" : "success"} />
                    </div>
                  </div>
                  {binderAttentionAnalyses.length > 0 && (
                    <Button
                      label={showBinderAnalysisWatchlist ? "Hide Watchlist" : "View Watchlist"}
                      icon={showBinderAnalysisWatchlist ? "pi pi-eye-slash" : "pi pi-list"}
                      severity="secondary"
                      text
                      onClick={() => setShowBinderAnalysisWatchlist((current) => !current)}
                    />
                  )}
                </div>

                {loadingBinderAnalysisWatchlist ? (
                  <div className="flex justify-content-center p-3">
                    <ProgressSpinner style={{ width: "36px", height: "36px" }} />
                  </div>
                ) : binderLatestAnalyses.length === 0 ? (
                  <div style={appNeutralPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>No binder AI reviews yet</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Analyze the latest version of binder files to track running jobs, stale reviews, failures, and low-confidence matches here.
                    </div>
                  </div>
                ) : binderAttentionAnalyses.length === 0 ? (
                  <div style={appAccentPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#166534" }}>No AI review attention items</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      The latest analyzed binder files are currently clear of running, stale, failed, or low-confidence flags.
                    </div>
                  </div>
                ) : !showBinderAnalysisWatchlist ? (
                  <div style={appNeutralPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>Watchlist hidden</div>
                    <div className="mt-2" style={appMutedTextStyle}>
                      Use <strong>View Watchlist</strong> to open the binder-wide AI review table when you want to triage attention items.
                    </div>
                  </div>
                ) : (
                  <div style={appDataTableWrapperStyle}>
                    <DataTable value={binderAttentionAnalyses} paginator rows={10} emptyMessage="No binder AI watchlist items found.">
                      <Column header="Document" body={(rowData: BinderDocumentAnalysis) => getAnalysisDocumentTitle(rowData)} />
                      <Column
                        header="Folder"
                        body={(rowData: BinderDocumentAnalysis) => rowData.folderName || getFolderName(binderDocuments.find((document) => document.binderDocumentId === rowData.binderDocumentId)?.folderId)}
                      />
                      <Column header="Status" body={(rowData: BinderDocumentAnalysis) => <Tag value={getAnalysisLabel(rowData.status)} severity={getAnalysisSeverity(rowData.status)} />} />
                      <Column header="Folder Match" body={(rowData: BinderDocumentAnalysis) => <Tag value={getFolderMatchLabel(rowData.likelySatisfiesFolder)} severity={getFolderMatchSeverity(rowData.likelySatisfiesFolder)} />} />
                      <Column header="Confidence" body={(rowData: BinderDocumentAnalysis) => rowData.confidenceScore ? `${rowData.confidenceScore}%` : "N/A"} />
                      <Column header="Rule Match" body={(rowData: BinderDocumentAnalysis) => rowData.exactRuleMatchConfidence ? `${rowData.exactRuleMatchConfidence}%` : "N/A"} />
                      <Column header="Reason" body={(rowData: BinderDocumentAnalysis) => rowData.reviewReasons?.[0] || rowData.recommendedAction || rowData.summary || "Needs manual review"} />
                      <Column
                        header="Action"
                        body={(rowData: BinderDocumentAnalysis) => (
                          <Button
                            label="View Analysis"
                            icon="pi pi-search"
                            className="p-button-outlined p-button-sm"
                            onClick={() => {
                              const binderDocument = binderDocuments.find((document) => document.binderDocumentId === rowData.binderDocumentId);
                              if (binderDocument) {
                                void openAnalysisDialog(binderDocument);
                              }
                            }}
                          />
                        )}
                      />
                    </DataTable>
                  </div>
                )}
              </div>
            </Card>
          )}

          <Card style={appSectionCardStyle}>
            {isCaregiverJobBinder && !isSharedBinder && binder && missingRequiredFolders.length > 0 ? (
              <div className="mb-3 p-3 border-round" style={{ background: "#fff7ed", border: "1px solid #fdba74", borderLeft: "4px solid #ea580c" }}>
                <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-3">
                  <div>
                    <div style={{ fontWeight: 700, color: "#9a3412" }}>Complete your Binder</div>
                    <div className="mt-1" style={{ color: "#7c2d12", lineHeight: 1.45 }}>
                      Add a document to each required folder marked with a red <strong>*</strong>. Select a folder, then click <strong>Add File</strong>.
                    </div>
                    <small className="block mt-1" style={{ color: "#9a3412" }}>
                      Missing: {missingRequiredFolders.map((folder) => folder.folderName || "Folder").join(", ")}
                    </small>
                  </div>
                  <Button
                    type="button"
                    label="Open missing folder"
                    icon="pi pi-folder-open"
                    severity="warning"
                    className="p-button-outlined p-button-sm"
                    onClick={() => {
                      const firstMissingFolder = missingRequiredFolders[0];
                      if (firstMissingFolder?.folderId) {
                        void loadFolderFiles(firstMissingFolder.folderId);
                      }
                    }}
                  />
                </div>
              </div>
            ) : null}
            <div className="flex flex-column md:flex-row md:align-items-start md:justify-content-between gap-3 mb-3">
              <div className="flex-1">
                {renderSurfaceHeader(isSharedBinder ? "Review Binder" : resolvedPageTitle, isSharedBinder ? "Review the Binder documents." : "", "pi pi-folder-open")}
                {isFacilityWorkerJobBinder && binder ? (
                  <div className="mt-2" style={appMutedTextStyle}>
                    <strong style={{ color: "#334155" }}>{authExperience.providerHome.facilityTypeLabel}:</strong> {binderFacilityTypeLabel}
                  </div>
                ) : null}
              </div>
              {isSharedBinder && sharedBinderShare ? (
                <Card className="w-full lg:w-6" style={{ background: "#fffaf0", border: "1px solid #fed7aa", borderLeft: "4px solid #f59e0b", boxShadow: "0 2px 8px rgba(180, 83, 9, 0.08)" }}>
                  <div className="grid" style={{ margin: "-0.25rem" }}>
                    <div className="col-12 md:col-6">
                      <div style={{ fontWeight: 700, color: "#9a3412", fontSize: "0.9rem" }}>Message from the User</div>
                      <div className="mt-2" style={{ color: "#334155", whiteSpace: "pre-wrap", lineHeight: 1.45 }}>{sharedBinderShare.message || "No message provided."}</div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div style={{ fontWeight: 700, color: "#9a3412", fontSize: "0.9rem" }}>Send message</div>
                      <InputTextarea
                        id="sharedBinderReviewNotes"
                        value={reviewNotes}
                        onChange={(event) => setReviewNotes(event.target.value)}
                        rows={3}
                        autoResize
                        placeholder="Message for the Binder owner"
                        className="w-full mt-2"
                      />
                      <Button
                        label="Notify Binder Owner"
                        icon="pi pi-send"
                        severity="warning"
                        className="p-button-sm mt-2"
                        onClick={() => void onUpdateSharedReview()}
                        disabled={savingSharedReview || !reviewNotes.trim()}
                      />
                    </div>
                  </div>
                </Card>
              ) : null}
              <div className="flex flex-wrap align-items-center justify-content-end gap-2">
                {folders.length > 10 ? (
                  <InputText
                    value={folderSearchQuery}
                    onChange={(event) => {
                      setFolderPageFirst(0);
                      setFolderSearchQuery(event.target.value);
                    }}
                    placeholder="Search folders"
                    aria-label="Search binder folders"
                    className="w-full md:w-auto"
                  />
                ) : null}
                {binder && !isSharedBinder ? (
                  <Button
                    type="button"
                    icon="pi pi-cog"
                    aria-label="Update Binder Config"
                    className="p-button-outlined p-button-sm"
                    tooltip="Update Binder Config"
                    tooltipOptions={{ position: "top" }}
                    onClick={() => {
                      const configuredFacilityType = String(binder.facilityType || selectedFacilityType || authExperience.providerSignup.defaultFacilityType || "afh").trim().toLowerCase();
                      setResetFacilityType(facilityTypeOptions.some((option) => option.value === configuredFacilityType)
                        ? configuredFacilityType
                        : facilityTypeOptions[0]?.value || configuredFacilityType);
                      setShowBinderConfigDialog(true);
                    }}
                  />
                ) : null}
                {isCaregiverJobBinder && binder && !isSharedBinder ? (
                  <>
                    <Button
                      label="Staff Credentials"
                      icon="pi pi-id-card"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => void openStaffCredentialsDialog()}
                      tooltip="View or update your Staff-table credentials"
                      tooltipOptions={{ position: "top" }}
                    />
                    <Button
                      label={`Share (${binderShares.length})`}
                      icon="pi pi-share-alt"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => openShareDialog()}
                      tooltip="Share specific binder folders to another person"
                      tooltipOptions={{ position: "top" }}
                    />
                    <Button
                      label="Add Folder"
                      icon="pi pi-plus"
                      severity="success"
                      className="p-button-outlined p-button-sm"
                      onClick={() => setShowCreateFolderDialog(true)}
                    />
                    <Button
                      type="button"
                      label={runningCompliance ? "Checking..." : "Check Binder Compliance"}
                      icon="pi pi-check-circle"
                      className="p-button-outlined p-button-sm"
                      onClick={() => void onRunCompliance()}
                      disabled={runningCompliance}
                      tooltip="Verify each required folder rules if the document has been added or not"
                      tooltipOptions={{ position: "top" }}
                    />
                  </>
                ) : null}
                {binder && !isSharedBinder && !isCaregiverJobBinder ? (
                  <>
                    <Button
                      label="Share"
                      icon="pi pi-share-alt"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => openShareDialog()}
                    />
                    <Button
                      label="Add Folder"
                      icon="pi pi-plus"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => setShowCreateFolderDialog(true)}
                    />
                    <Button
                      type="button"
                      label={runningCompliance ? "Checking..." : "Check Binder Compliance"}
                      icon="pi pi-check-circle"
                      className="p-button-outlined p-button-sm"
                      onClick={() => void onRunCompliance()}
                      disabled={runningCompliance}
                      tooltip="Check Binder files against the applicable rules"
                      tooltipOptions={{ position: "top" }}
                    />
                    <Button
                      label="Upload File"
                      icon="pi pi-upload"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => openUploadDialog(activeFolderId || undefined)}
                    />
                    <Button
                      label="Open Documents"
                      icon="pi pi-folder-open"
                      severity="warning"
                      className="p-button-outlined p-button-sm"
                      onClick={() => navigate(documentsPagePath)}
                    />
                  </>
                ) : null}
              </div>
            </div>
            <div className="grid" style={{ minWidth: 0 }}>
              <div className="col-12 lg:col-3">
                <div className="flex flex-column gap-3">
                  <div className="flex align-items-center justify-content-between gap-2">
                    <strong>Folders ({folders.length})</strong>
                  </div>
                  <div style={appDataTableWrapperStyle}>
                    <DataTable
                      value={sortedVisibleFolders}
                      dataKey="folderId"
                      tableStyle={{ width: "100%", tableLayout: "fixed" }}
                      selection={activeFolder}
                      onSelectionChange={(event) => {
                        const folder = event.value as BinderFolder;
                        if (folder?.folderId) {
                          void loadFolderFiles(folder.folderId);
                        }
                      }}
                      selectionMode="single"
                      first={folderPageFirst}
                      onPage={(event) => {
                        setFolderPageFirst(event.first);
                        setFolderTooltipRefreshKey((currentKey) => currentKey + 1);
                      }}
                      paginator={visibleFolders.length > 15}
                      rows={15}
                      emptyMessage={folderSearchQuery ? "No matching folders." : "No binder folders found."}
                      rowClassName={(rowData: BinderFolder) => rowData.folderId === activeFolderId ? "bg-blue-50" : ""}
                    >
                      <Column
                        header={(
                          <div className="flex align-items-center gap-1" style={{ width: "100%", display: "flex" }}>
                            <span>Folder</span>
                            <div className="flex align-items-center gap-0" style={{ marginLeft: "auto" }}>
                              <Button type="button" icon={folderSortIcon("name")} className="p-button-text p-button-rounded p-button-sm" style={{ width: "1.7rem", height: "1.7rem", minWidth: 0, padding: 0 }} tooltip="Sort by name" tooltipOptions={{ position: "top" }} aria-label="Sort folders by name" onClick={(event) => { event.stopPropagation(); toggleFolderSort("name"); }} />
                              <Button type="button" icon={folderSortIcon("required")} className="p-button-text p-button-rounded p-button-sm" style={{ width: "1.7rem", height: "1.7rem", minWidth: 0, padding: 0 }} tooltip="Sort by required" tooltipOptions={{ position: "top" }} aria-label="Sort folders by required" onClick={(event) => { event.stopPropagation(); toggleFolderSort("required"); }} />
                              <Button type="button" icon={folderSortIcon("private")} className="p-button-text p-button-rounded p-button-sm" style={{ width: "1.7rem", height: "1.7rem", minWidth: 0, padding: 0 }} tooltip="Sort by private" tooltipOptions={{ position: "top" }} aria-label="Sort folders by private" onClick={(event) => { event.stopPropagation(); toggleFolderSort("private"); }} />
                            </div>
                          </div>
                        )}
                        style={{ width: "100%", padding: "0.45rem 0.6rem", minWidth: 0 }}
                        headerStyle={{ width: "100%" }}
                        body={(rowData: BinderFolder) => (
                          <div className="flex align-items-start gap-2" style={{ width: "100%", minWidth: 0 }}>
                            <i className="pi pi-folder" style={{ flex: "0 0 auto", color: "#b45309", marginTop: "0.2rem" }} />
                            <div style={{ flex: "1 1 auto", minWidth: 0 }}>{renderFolderName(rowData)}</div>
                          </div>
                        )}
                      />
                    </DataTable>
                  </div>
                </div>
              </div>

              <div className="col-12 lg:col-9" ref={folderFilesSectionRef}>
                <div className="flex flex-column gap-3">
                  <div className="flex flex-column md:flex-row md:align-items-center md:justify-content-between gap-2">
                    <div>
                      <strong>{activeFolder ? `${activeFolder.folderName} Files` : "Folder Files"}</strong>
                    </div>
                    <div className="flex flex-wrap align-items-center gap-2">
                      {!isSharedBinder && activeFolder && activeFolderDocuments.length > 0 ? (
                        <Button
                          label="Add File"
                          icon="pi pi-upload"
                          severity="warning"
                          className="p-button-outlined p-button-sm"
                          onClick={() => openUploadDialog(activeFolder.folderId)}
                        />
                      ) : null}
                    </div>
                  </div>
                  {activeFolder && activeFolderExpiryAttentionCount > 0 ? (
                    <div className="mb-3">
                      <div className="flex flex-wrap gap-1">
                        {expiredActiveFolderDocuments.length > 0 ? <Tag value={`${expiredActiveFolderDocuments.length} expired`} severity="danger" rounded /> : null}
                        {expiringSoonActiveFolderDocuments.length > 0 ? <Tag value={`${expiringSoonActiveFolderDocuments.length} expiring soon`} severity="warning" rounded /> : null}
                      </div>
                    </div>
                  ) : null}
                  {activeFolder?.guidanceTitle ? (
                    <div className="p-3 border-round" style={{ background: "#fffbeb", border: "1px solid #fde68a" }}>
                      {renderFolderGuidance(activeFolder)}
                    </div>
                  ) : null}
                  {!isSharedBinder && isCaregiverJobBinder && !!documentsNeedingUpdates.length && (
                    <div className="mb-3" style={appNeutralPanelStyle}>
                      <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Documents That Need Updates</div>
                          <div style={appMutedTextStyle}>
                            {documentsNeedingUpdates.length === 1
                              ? "One file in this folder needs an updated upload from you."
                              : `${documentsNeedingUpdates.length} files in this folder need updated uploads from you.`}
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {documentsNeedingUpdates.slice(0, 3).map((document) => (
                            <Button
                              key={document.binderDocumentId || document.title}
                              type="button"
                              label={`Upload Update: ${document.title || "Document"}`}
                              icon="pi pi-upload"
                              severity="warning"
                              className="p-button-outlined p-button-sm"
                              onClick={() => openVersionDialog(document)}
                            />
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                  {loadingFolderFiles || activeFolderDocuments.length > 0 ? (
                    <div style={appDataTableWrapperStyle}>
                      <DataTable value={activeFolderDocuments} paginator rows={10} loading={loadingFolderFiles} emptyMessage="No files in this folder yet.">
                        <Column header="Document" body={renderDocumentTitle} />
                        <Column header="Uploaded" body={renderLastUpdated} />
                        <Column header="Uploaded By" body={renderUploadedBy} />
                        {activeFolderDocuments.some((document) => String(document.expirationDate || "").trim()) ? (
                          <Column header="Expires" body={renderDocumentExpirationDate} />
                        ) : null}
                        <Column header="Actions" body={renderDocumentActions} />
                      </DataTable>
                    </div>
                  ) : activeFolder ? (
                    <div className="flex flex-column align-items-center justify-content-center gap-2 p-5 text-center border-1 border-round" style={{ minHeight: "12rem", borderColor: "#e2e8f0", background: "#f8fafc" }}>
                      <i className="pi pi-file text-3xl" style={{ color: "#94a3b8" }} />
                      <strong style={{ color: "#334155" }}>No files in this folder yet.</strong>
                      {!isSharedBinder ? (
                        <Button
                          label="Add File"
                          icon="pi pi-upload"
                          severity="warning"
                          className="p-button-sm mt-1"
                          onClick={() => openUploadDialog(activeFolder.folderId)}
                        />
                      ) : null}
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          </Card>

          {isSharedBinder && sharedBinderShare && (
            <>
              {!!sharedBinderShare.history?.length && (
                <Card style={appSectionCardStyle}>
                  <div style={appNeutralPanelStyle}>
                    <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Activity History</div>
                    <div className="flex flex-column gap-3">
                      {sharedBinderShare.history.map((historyItem: any, index: number) => (
                        <div key={`${historyItem.eventType || "event"}-${historyItem.createdAt || index}`} style={{ borderLeft: "3px solid #f59e0b", paddingLeft: "0.85rem" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>{historyItem.title || "Update"}</div>
                          <div style={appMutedTextStyle}>{historyItem.description || ""}</div>
                          <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.2rem" }}>
                            {historyItem.actorLabel || "System"}{historyItem.createdAt ? ` ${formatDateTime(historyItem.createdAt)}` : ""}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </Card>
              )}
            </>
          )}

          <Dialog
            header={renderSurfaceHeader(selectedNoteFolder?.folderName || "Folder notes", "Notes shared with the Binder owner.", "pi pi-comment")}
            visible={showFolderNoteDialog}
            modal
            onHide={() => !savingFolderNote && setShowFolderNoteDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div className="flex flex-column gap-3">
              {getFolderNotes(selectedNoteFolder?.folderId).length > 0 ? (
                getFolderNotes(selectedNoteFolder?.folderId).map((note: BinderFolderNote) => (
                  <div key={note.noteId} style={appNeutralPanelStyle}>
                    <div style={{ fontWeight: 700 }}>{note.createdByName || note.createdByEmail || "Hub user"}</div>
                    <div className="mt-1">{note.note}</div>
                    <small className="mt-1 block" style={appMutedTextStyle}>{formatDateTime(note.createdAt)}</small>
                  </div>
                ))
              ) : (
                <div style={appMutedTextStyle}>No notes on this folder yet.</div>
              )}
              {isSharedBinder ? (
                <>
                  <InputTextarea
                    value={folderNoteDraft}
                    onChange={(event) => setFolderNoteDraft(event.target.value)}
                    rows={4}
                    autoResize
                    placeholder="Add a note for the Binder owner"
                    className="w-full"
                  />
                  <div className="flex justify-content-end">
                    <Button
                      label={savingFolderNote ? "Saving..." : "Add Note"}
                      icon="pi pi-comment"
                      severity="warning"
                      tooltip="Add any note here and it will be visible to the Binder owner."
                      tooltipOptions={{ position: "top" }}
                      onClick={() => void onSaveFolderNote()}
                      disabled={savingFolderNote || !folderNoteDraft.trim()}
                    />
                  </div>
                </>
              ) : null}
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Share Binder", "Share this Binder with another Hub user by email.", "pi pi-share-alt")}
            visible={showShareDialog}
            modal
            onHide={() => !sharingBinder && setShowShareDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            {(showNewShareForm || binderShares.length === 0) && <>
              <Card style={{ ...appSectionCardStyle, marginBottom: "1rem" }}>
                {renderSurfaceHeader(
                  shareDialogStep === 1 ? "Recipient details" : "Confirm Binder share",
                  shareDialogStep === 1 ? "Choose the recipient, folders, and access period." : "Review exactly what will be shared before sending.",
                  shareDialogStep === 1 ? "pi pi-envelope" : "pi pi-check-circle",
                )}

                {shareDialogStep === 1 ? <>
                  <div className="grid mt-2">
                    <div className="col-12 md:col-6">
                      <div style={appFormSectionStyle}>
                        <label htmlFor="binderShareRecipientEmail" className="mb-2 block"><strong>Recipient email address</strong></label>
                        <InputText
                          id="binderShareRecipientEmail"
                          value={shareRecipientEmail}
                          onChange={(event) => setShareRecipientEmail(event.target.value)}
                          placeholder="recipient@example.com"
                          className="w-full"
                        />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div style={appFormSectionStyle}>
                        <label htmlFor="binderShareRecipientName" className="mb-2 block"><strong>Recipient name</strong></label>
                        <InputText
                          id="binderShareRecipientName"
                          value={shareRecipientName}
                          onChange={(event) => setShareRecipientName(event.target.value)}
                          placeholder="Recipient name"
                          className="w-full"
                        />
                      </div>
                    </div>
                  </div>
                  <div style={appFormSectionStyle}>
                    <label htmlFor="binderShareMessage" className="mb-2 block"><strong>Message to recipient</strong></label>
                    <InputTextarea
                      id="binderShareMessage"
                      value={shareMessage}
                      onChange={(event) => setShareMessage(event.target.value)}
                      rows={4}
                      autoResize
                      placeholder="Optional note for the recipient"
                      className="w-full"
                    />
                  </div>
                  <div style={appFormSectionStyle}>
                    <div className="flex align-items-center justify-content-between gap-2 mb-2">
                      <strong>Folders to share</strong>
                      <Button
                        type="button"
                        label={selectedShareFolderIds.length === shareableFolders.length ? "Clear all" : "Select all"}
                        className="p-button-text p-button-sm"
                        onClick={() => setSelectedShareFolderIds(selectedShareFolderIds.length === shareableFolders.length ? [] : shareableFolders.map((folder) => folder.folderId || "").filter(Boolean))}
                      />
                    </div>
                    <div className="flex flex-column gap-2" style={{ maxHeight: "14rem", overflowY: "auto" }}>
                      {shareableFolders.map((folder) => {
                        const folderId = folder.folderId || "";
                        return (
                          <div key={folderId} className="flex align-items-center gap-2">
                            <Checkbox
                              inputId={`share-folder-${folderId}`}
                              checked={selectedShareFolderIds.includes(folderId)}
                              onChange={(event) => setSelectedShareFolderIds((current) => event.checked ? [...current, folderId] : current.filter((id) => id !== folderId))}
                            />
                            <label htmlFor={`share-folder-${folderId}`}>{folder.folderName || "Untitled folder"}</label>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  <div style={appFormSectionStyle}>
                    <label htmlFor="binderShareExpiration" className="mb-2 block"><strong>Access expires</strong></label>
                    <Dropdown
                      id="binderShareExpiration"
                      value={shareExpirationOption}
                      options={shareExpirationOptions}
                      onChange={(event) => setShareExpirationOption(String(event.value || "7"))}
                      className="w-full"
                    />
                  </div>
                  <div className="mt-3 flex justify-content-end">
                    <Button
                      label="Review Share"
                      icon="pi pi-arrow-right"
                      severity="warning"
                      onClick={reviewShare}
                    />
                  </div>
                </> : <>
                  <div style={appAccentPanelStyle} className="mt-3">
                    <div><strong>Recipient</strong><div className="mt-1">{shareRecipientName.trim() || "Hub user"} ({shareRecipientEmail.trim().toLowerCase()})</div></div>
                    <div className="mt-3">
                      <strong>Folders ({selectedShareFolderIds.length})</strong>
                      <div className="mt-2" style={appDataTableWrapperStyle}>
                        <DataTable
                          value={Array.from({ length: Math.ceil(selectedShareFolderIds.length / 4) }, (_, rowIndex) => {
                            const selectedFolders = shareableFolders.filter((folder) => selectedShareFolderIds.includes(folder.folderId || ""));
                            return {
                              column1: selectedFolders[rowIndex * 4]?.folderName || "",
                              column2: selectedFolders[rowIndex * 4 + 1]?.folderName || "",
                              column3: selectedFolders[rowIndex * 4 + 2]?.folderName || "",
                              column4: selectedFolders[rowIndex * 4 + 3]?.folderName || "",
                            };
                          })}
                          showGridlines
                          showHeaders={false}
                          size="small"
                          emptyMessage="No folders selected"
                        >
                          <Column field="column1" />
                          <Column field="column2" />
                          <Column field="column3" />
                          <Column field="column4" />
                        </DataTable>
                      </div>
                    </div>
                    <div className="mt-3"><strong>Access expires</strong><div className="mt-1">{getShareExpirationLabel()} ({getShareExpirationDate()})</div></div>
                  </div>
                  <div className="mt-3 flex justify-content-end gap-2">
                    <Button label="Back" icon="pi pi-arrow-left" className="p-button-outlined" onClick={() => setShareDialogStep(1)} disabled={sharingBinder} />
                    <Button label={sharingBinder ? "Sharing..." : "Confirm & Share"} icon="pi pi-share-alt" severity="warning" onClick={() => void onShareBinder()} disabled={sharingBinder} />
                  </div>
                </>}
              </Card>
            </>}

            {binderShares.length > 0 && !showNewShareForm && <Card style={appSectionCardStyle}>
              <div className="flex align-items-center justify-content-between gap-2">
                {renderSurfaceHeader("Shared with users", "Review or remove existing Binder shares.", "pi pi-users")}
                <Button
                  label="New Share"
                  icon="pi pi-plus"
                  severity="warning"
                  className="p-button-outlined p-button-sm"
                  onClick={() => {
                    openShareDialog();
                    setShowNewShareForm(true);
                  }}
                />
              </div>
              <div className="mt-3" style={appDataTableWrapperStyle}>
                <DataTable
                  value={binderShares}
                  loading={loadingBinderShares}
                  responsiveLayout="scroll"
                  emptyMessage="You have not shared this Binder with anyone yet."
                >
                  <Column field="recipientName" header="Name" body={(rowData: BinderShare) => rowData.recipientName || "Not provided"} />
                  <Column field="recipientEmail" header="Email" />
                  <Column
                    header="Status"
                    body={(rowData: BinderShare) => {
                      const expiryStatus = rowData.expirationDate ? getExpiryStatus(rowData.expirationDate) : { label: "Forever", severity: "info" as const };
                      return (
                        <div className="flex flex-wrap align-items-center gap-1">
                          <Tag
                            value={`Expires: ${rowData.expirationDate ? formatDateTime(rowData.expirationDate) : "Forever"}`}
                            icon="pi pi-clock"
                            severity={expiryStatus.severity}
                            rounded
                          />
                          <Tag
                            value={`Sent: ${formatDateTime(rowData.createdAt)}`}
                            icon="pi pi-send"
                            severity="info"
                            rounded
                          />
                        </div>
                      );
                    }}
                  />
                  <Column
                    header="Action"
                    body={(rowData: BinderShare) => (
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          icon="pi pi-pencil"
                          severity="warning"
                          className="p-button-outlined p-button-rounded p-button-sm"
                          tooltip="Edit shared folders"
                          tooltipOptions={{ position: "top" }}
                          aria-label="Edit shared folders"
                          onClick={() => openEditShareFoldersDialog(rowData)}
                        />
                        <Button
                          type="button"
                          icon="pi pi-times"
                          severity="danger"
                          className="p-button-outlined p-button-rounded p-button-sm"
                          tooltip="Unshare Binder"
                          tooltipOptions={{ position: "top" }}
                          aria-label="Unshare Binder"
                          onClick={() => onUnshareBinder(rowData)}
                        />
                      </div>
                    )}
                  />
                </DataTable>
              </div>
            </Card>}

            <Dialog
              header={renderSurfaceHeader("Edit Shared Folders", "Choose which folders this Binder share can access.", "pi pi-folder")}
              visible={showEditShareFoldersDialog}
              modal
              onHide={() => !savingShareFolders && setShowEditShareFoldersDialog(false)}
              style={appDialogStyle}
              headerStyle={appDialogHeaderStyle}
              contentStyle={appDialogContentStyle}
            >
              <div className="flex flex-column gap-2">
                {shareableFolders.map((folder) => {
                  const folderId = folder.folderId || "";
                  return (
                    <div key={folderId} className="flex align-items-center gap-2">
                      <Checkbox
                        inputId={`edit-share-folder-${folderId}`}
                        checked={selectedShareFolderIds.includes(folderId)}
                        onChange={(event) => setSelectedShareFolderIds((current) => event.checked
                          ? [...current, folderId]
                          : current.filter((id) => id !== folderId))}
                      />
                      <label htmlFor={`edit-share-folder-${folderId}`}>{folder.folderName || "Untitled folder"}</label>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex justify-content-end gap-2">
                <Button label="Cancel" className="p-button-outlined" onClick={() => setShowEditShareFoldersDialog(false)} disabled={savingShareFolders} />
                <Button label={savingShareFolders ? "Saving..." : "Save Folders"} icon="pi pi-check" severity="warning" onClick={() => void onSaveShareFolders()} disabled={savingShareFolders || !selectedShareFolderIds.length} />
              </div>
            </Dialog>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowShareDialog(false)}
                disabled={sharingBinder}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Answer Job Questions", "Send your answers back to the provider.", "pi pi-comments")}
            visible={showAnswerQuestionsDialog}
            modal
            onHide={() => !savingJobQuestionAnswers && setShowAnswerQuestionsDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appNeutralPanelStyle} className="mb-3">
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Provider Questions</div>
              <div className="mt-2" style={appMutedTextStyle}>{selectedQuestionsShare?.jobQuestions || "No questions available."}</div>
            </div>

            <div style={appFormSectionStyle}>
              <label htmlFor="binderJobQuestionAnswers" className="mb-2 block"><strong>Your Answers</strong></label>
              <InputTextarea
                id="binderJobQuestionAnswers"
                value={jobQuestionAnswersDraft}
                onChange={(event) => setJobQuestionAnswersDraft(event.target.value)}
                rows={6}
                autoResize
                placeholder="Enter your answers for the provider"
                className="w-full"
              />
            </div>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowAnswerQuestionsDialog(false)}
                disabled={savingJobQuestionAnswers}
              />
              <Button
                label={savingJobQuestionAnswers ? "Sending..." : "Send Answers"}
                icon="pi pi-send"
                severity="warning"
                onClick={() => void onSubmitJobQuestionAnswers()}
                disabled={savingJobQuestionAnswers}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Review File", "Leave a simple note for this document.", "pi pi-comment")}
            visible={showReviewDocumentDialog}
            modal
            onHide={() => !savingReviewDocument && setShowReviewDocumentDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appFormSectionStyle}>
              <label htmlFor="binderReviewDocumentStatus" className="mb-2 block"><strong>File Status</strong></label>
              <Dropdown
                id="binderReviewDocumentStatus"
                value={reviewDocumentStatus}
                options={[
                  { label: "Not Reviewed", value: "" },
                  { label: "Looks Good", value: "looks_good" },
                  { label: "Need Updates", value: "needs_update" },
                ]}
                onChange={(event) => setReviewDocumentStatus(event.value)}
                className="w-full"
              />
            </div>

            <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
              <label htmlFor="binderReviewDocumentNote" className="mb-2 block"><strong>File Note</strong></label>
              <InputTextarea
                id="binderReviewDocumentNote"
                value={reviewDocumentNote}
                onChange={(event) => setReviewDocumentNote(event.target.value)}
                rows={5}
                autoResize
                placeholder="Example: Please upload a clearer copy or your most recent card."
                className="w-full"
              />
            </div>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowReviewDocumentDialog(false)}
                disabled={savingReviewDocument}
              />
              <Button
                label={savingReviewDocument ? "Saving..." : "Save File Review"}
                icon="pi pi-check"
                severity="warning"
                onClick={() => void onSaveReviewDocument()}
                disabled={savingReviewDocument}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Share History", "See the main steps for this provider share.", "pi pi-history")}
            visible={showShareHistoryDialog}
            modal
            onHide={() => setShowShareHistoryDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            {selectedHistoryShare?.history?.length ? (
              <div className="flex flex-column gap-3">
                {selectedHistoryShare.history.map((historyItem: any, index: number) => (
                  <div key={`${historyItem.eventType || "event"}-${historyItem.createdAt || index}`} style={{ borderLeft: "3px solid #f59e0b", paddingLeft: "0.85rem" }}>
                    <div style={{ fontWeight: 700, color: "#0f172a" }}>{historyItem.title || "Update"}</div>
                    <div style={appMutedTextStyle}>{historyItem.description || ""}</div>
                    <div style={{ ...appMutedTextStyle, fontSize: "0.82rem", marginTop: "0.2rem" }}>
                      {historyItem.actorLabel || "System"}{historyItem.createdAt ? ` • ${formatDateTime(historyItem.createdAt)}` : ""}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={appNeutralPanelStyle}>No history yet.</div>
            )}
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Save Question Template", "Save this question set so you can reuse it on other caregiver applications.", "pi pi-save")}
            visible={showSaveJobQuestionTemplateDialog}
            modal
            onHide={() => !savingJobQuestionTemplate && setShowSaveJobQuestionTemplateDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appFormSectionStyle}>
              <label htmlFor="binderJobQuestionTemplateName" className="mb-2 block"><strong>Template Name</strong></label>
              <InputText
                id="binderJobQuestionTemplateName"
                value={jobQuestionTemplateName}
                onChange={(event) => setJobQuestionTemplateName(event.target.value)}
                placeholder="Example: Standard caregiver screening"
                className="w-full"
              />
            </div>
            <div className="flex justify-content-end gap-2">
              <Button
                type="button"
                label="Cancel"
                severity="secondary"
                className="p-button-outlined"
                onClick={() => setShowSaveJobQuestionTemplateDialog(false)}
                disabled={savingJobQuestionTemplate}
              />
              <Button
                type="button"
                label={savingJobQuestionTemplate ? "Saving..." : "Save Template"}
                icon="pi pi-save"
                severity="warning"
                onClick={() => void onSaveCurrentJobQuestionsAsTemplate()}
                disabled={savingJobQuestionTemplate}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Send Fax", "Enter the fax number for this document.", "pi pi-send")}
            visible={showSendFaxDialog}
            modal
            onHide={() => !sendingFax && setShowSendFaxDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div className="flex flex-column gap-3">
              <div style={appNeutralPanelStyle}>
                <strong>{selectedFaxVersion?.fileName || selectedFaxDocument?.title || "Binder document"}</strong>
                <div className="mt-1" style={appMutedTextStyle}>The current file will be sent to the number below.</div>
              </div>
              <div>
                <label htmlFor="binderFaxNumber" className="mb-2 block"><strong>Fax Number</strong></label>
                <InputText
                  id="binderFaxNumber"
                  value={faxNumber}
                  onChange={(event) => setFaxNumber(event.target.value)}
                  placeholder="(555) 555-5555"
                  inputMode="tel"
                  className="w-full"
                  autoFocus
                />
                <small style={appMutedTextStyle}>Use a 10-digit U.S. or Canada fax number.</small>
              </div>
              <div className="flex justify-content-end gap-2">
                <Button
                  label="Cancel"
                  className="p-button-outlined"
                  onClick={() => setShowSendFaxDialog(false)}
                  disabled={sendingFax}
                />
                <Button
                  label={sendingFax ? "Sending..." : "Send Fax"}
                  icon="pi pi-send"
                  severity="warning"
                  onClick={() => void onSendFax()}
                  disabled={sendingFax || !faxNumber.trim()}
                />
              </div>
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Add File", "Choose a folder, optional dates, and the file to upload.", "pi pi-upload")}
            visible={showUploadDialog}
            modal
            onHide={() => !uploadingDocument && setShowUploadDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
              <label htmlFor="binderFileInput" className="mb-2 block"><strong>File</strong></label>
              <FileUpload
                ref={fileUploadRef}
                mode="basic"
                name="binderFileInput"
                customUpload
                auto={false}
                multiple={false}
                maxFileSize={APP_DOCUMENT_FILE_SIZE_IN_BYTES}
                onSelect={onFileSelected}
                onClear={() => setSelectedUploadFile(null)}
                chooseOptions={{
                  label: selectedUploadFile ? "Replace File" : "Choose File",
                  icon: "pi pi-upload",
                  className: "p-button-outlined w-full justify-content-center",
                }}
                className="w-full"
              />
              {selectedUploadFile && (
                <div className="mt-2" style={appMutedTextStyle}>
                  Selected: <strong>{selectedUploadFile.name}</strong>
                </div>
              )}
            </div>
            <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
              <label htmlFor="binderDocumentNumber" className="mb-2 block">
                <strong>Document Number</strong> <small style={appMutedTextStyle}>(Optional)</small>
              </label>
              <InputText
                id="binderDocumentNumber"
                value={uploadDocumentNumber}
                onChange={(event) => setUploadDocumentNumber(event.target.value)}
                placeholder="Enter document or certificate number"
                className="w-full"
              />
            </div>
            {isIdentityWorkEligibilityFolder(selectedUploadFolder) ? (
              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <label htmlFor="binderDateOfBirth" className="mb-2 block"><strong>Date of Birth</strong></label>
                <Calendar
                  id="binderDateOfBirth"
                  value={uploadDateOfBirth}
                  onChange={(event) => setUploadDateOfBirth((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                  dateFormat="mm/dd/yy"
                  className="w-full"
                  inputClassName="w-full"
                  placeholder="Select date of birth"
                  maxDate={new Date()}
                />
              </div>
            ) : null}
            <div className="grid mt-1">
              <div className="col-12 md:col-6">
                <div style={appFormSectionStyle}>
                  <label htmlFor="binderEffectiveDate" className="mb-2 block"><strong>Effective Date</strong></label>
                  <Calendar
                    id="binderEffectiveDate"
                    value={uploadEffectiveDate}
                    onChange={(event) => setUploadEffectiveDate((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                    dateFormat="mm/dd/yy"
                    className="w-full"
                    inputClassName="w-full"
                    placeholder="Select effective date"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div style={appFormSectionStyle}>
                  <label htmlFor="binderExpirationDate" className="mb-2 block"><strong>Expiry Date</strong></label>
                  <Calendar
                    id="binderExpirationDate"
                    value={uploadExpirationDate}
                    onChange={(event) => setUploadExpirationDate((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                    dateFormat="mm/dd/yy"
                    className="w-full"
                    inputClassName="w-full"
                    placeholder="Select expiry date"
                  />
                </div>
              </div>
            </div>

            <small className="block mt-3" style={{ ...appMutedTextStyle, color: "#9a3412" }}>
              Add dates or a document number when available to help track this file.
            </small>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowUploadDialog(false)}
                disabled={uploadingDocument}
              />
              <Button
                label={uploadingDocument ? "Uploading..." : "Upload"}
                icon="pi pi-check"
                severity="warning"
                onClick={() => void onUploadBinderFile()}
                disabled={uploadingDocument}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader(
              selectedEditDocument ? `Edit Binder Document: ${selectedEditDocument.title || "Binder File"}` : "Edit Binder Document",
              "Rename the record or move it to another binder folder.",
              "pi pi-pencil",
            )}
            visible={showEditDocumentDialog}
            modal
            onHide={() => !savingDocumentChanges && setShowEditDocumentDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appFormSectionStyle}>
              <label htmlFor="editBinderDocumentTitle" className="mb-2 block"><strong>Document Title</strong></label>
              <InputText
                id="editBinderDocumentTitle"
                value={editDocumentTitle}
                onChange={(e) => setEditDocumentTitle(e.target.value)}
                placeholder="Update binder document title"
                className="w-full"
              />
            </div>

            {getDocumentTypeOptionsForFolder(editDocumentFolderId).length ? (
              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <label htmlFor="editBinderDocumentType" className="mb-2 block"><strong>Document Type</strong></label>
                <Dropdown
                  id="editBinderDocumentType"
                  value={editDocumentType}
                  options={getDocumentTypeOptionsForFolder(editDocumentFolderId)}
                  onChange={(event) => {
                    const nextValue = String(event.value || "");
                    setEditDocumentType(nextValue);
                    if (!editDocumentTitle.trim() || editDocumentTitle === selectedEditDocument?.documentCategory) {
                      setEditDocumentTitle(nextValue);
                    }
                  }}
                  placeholder="Select document type"
                  className="w-full"
                />
              </div>
            ) : null}

            <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
              <label htmlFor="editBinderDocumentNumber" className="mb-2 block">
                <strong>Document Number</strong> <small style={appMutedTextStyle}>(Optional)</small>
              </label>
              <InputText
                id="editBinderDocumentNumber"
                value={editDocumentNumber}
                onChange={(event) => setEditDocumentNumber(event.target.value)}
                placeholder="Enter document or certificate number"
                className="w-full"
              />
            </div>
            {isIdentityWorkEligibilityFolder(selectedEditFolder) ? (
              <div style={{ ...appFormSectionStyle, marginTop: "1rem" }}>
                <label htmlFor="editBinderDateOfBirth" className="mb-2 block"><strong>Date of Birth</strong></label>
                <Calendar
                  id="editBinderDateOfBirth"
                  value={editDateOfBirth}
                  onChange={(event) => setEditDateOfBirth((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                  dateFormat="mm/dd/yy"
                  className="w-full"
                  inputClassName="w-full"
                  placeholder="Select date of birth"
                  maxDate={new Date()}
                />
              </div>
            ) : null}

            <div className="grid mt-1">
              <div className="col-12 md:col-6">
                <div style={appFormSectionStyle}>
                  <label htmlFor="editBinderEffectiveDate" className="mb-2 block"><strong>Effective Date</strong></label>
                  <Calendar
                    id="editBinderEffectiveDate"
                    value={editEffectiveDate}
                    onChange={(event) => setEditEffectiveDate((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    className="w-full"
                  />
                </div>
              </div>
              <div className="col-12 md:col-6">
                <div style={appFormSectionStyle}>
                  <label htmlFor="editBinderExpirationDate" className="mb-2 block"><strong>Expiry Date</strong></label>
                  <Calendar
                    id="editBinderExpirationDate"
                    value={editExpirationDate}
                    onChange={(event) => setEditExpirationDate((Array.isArray(event.value) ? event.value[0] : event.value) as Date | null)}
                    showIcon
                    dateFormat="mm/dd/yy"
                    className="w-full"
                  />
                </div>
              </div>
            </div>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowEditDocumentDialog(false)}
                disabled={savingDocumentChanges}
              />
              <Button
                label={savingDocumentChanges ? "Saving..." : "Save"}
                icon="pi pi-check"
                severity="warning"
                onClick={() => void onSaveDocumentChanges()}
                disabled={savingDocumentChanges}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader(
              selectedVersionDocument ? `Upload New Version: ${selectedVersionDocument.title || "Binder File"}` : "Upload New Version",
              "Add the next version to this binder document.",
              "pi pi-upload",
            )}
            visible={showVersionDialog}
            modal
            onHide={() => !uploadingVersion && setShowVersionDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appAccentPanelStyle} className="mb-3">
              <div style={{ fontWeight: 700, color: "#9a3412" }}>Version update</div>
              <div className="mt-2" style={appMutedTextStyle}>
                This keeps the same binder file and adds a newer version to its history.
              </div>
            </div>

            <div style={appFormSectionStyle}>
              <label htmlFor="binderVersionFileInput" className="mb-2 block"><strong>File</strong></label>
              <FileUpload
                ref={versionFileUploadRef}
                mode="basic"
                name="binderVersionFileInput"
                customUpload
                auto={false}
                multiple={false}
                maxFileSize={APP_DOCUMENT_FILE_SIZE_IN_BYTES}
                onSelect={onVersionFileSelected}
                onClear={() => setSelectedVersionFile(null)}
                chooseOptions={{
                  label: selectedVersionFile ? "Replace File" : "Choose File",
                  icon: "pi pi-upload",
                  className: "p-button-outlined w-full justify-content-center",
                }}
                className="w-full"
              />
              {selectedVersionFile && (
                <div className="mt-2" style={appMutedTextStyle}>
                  Selected: <strong>{selectedVersionFile.name}</strong>
                </div>
              )}
            </div>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowVersionDialog(false)}
                disabled={uploadingVersion}
              />
              <Button
                label={uploadingVersion ? "Uploading..." : "Upload Version"}
                icon="pi pi-check"
                severity="warning"
                onClick={() => void onUploadNewVersion()}
                disabled={uploadingVersion}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader(
              selectedVersionHistoryDocument ? `${selectedVersionHistoryDocument.title || "Binder File"} Versions` : "Version History",
              "Open or download previous versions from here.",
              "pi pi-history",
            )}
            visible={showVersionHistoryDialog}
            modal
            onHide={() => setShowVersionHistoryDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appDataTableWrapperStyle}>
              <DataTable
                value={selectedVersionHistoryRows}
                paginator
                rows={10}
                loading={loadingVersionHistory}
                emptyMessage="No versions found for this binder file."
              >
                <Column field="versionNumber" header="Version" body={(rowData: BinderDocumentVersion) => `v${rowData.versionNumber || ""}`} />
                <Column field="fileName" header="File" />
                <Column header="Uploaded" body={(rowData: BinderDocumentVersion) => formatDateTime(rowData.uploadedAt)} />
                <Column field="uploadedBy" header="Uploaded By" />
                <Column
                  header="Status"
                  body={(rowData: BinderDocumentVersion) => (
                    <Tag
                      value={rowData.versionNumber === selectedVersionHistoryDocument?.latestVersionNumber ? "Latest" : "Previous"}
                      severity={rowData.versionNumber === selectedVersionHistoryDocument?.latestVersionNumber ? "success" : "info"}
                    />
                  )}
                />
                <Column
                  header="Action"
                  body={(rowData: BinderDocumentVersion) => (
                    <div className="flex gap-2">
                      <Button
                        label="Preview"
                        icon="pi pi-eye"
                        className="p-button-outlined p-button-sm"
                        onClick={() => {
                          if (selectedVersionHistoryDocument) {
                            void onPreviewVersion(selectedVersionHistoryDocument, rowData);
                          }
                        }}
                      />
                      <Button
                        label="Open"
                        icon="pi pi-download"
                        className="p-button-outlined p-button-sm"
                        onClick={() => {
                          if (selectedVersionHistoryDocument) {
                            void onOpenSpecificVersion(selectedVersionHistoryDocument, rowData);
                          }
                        }}
                      />
                    </div>
                  )}
                />
              </DataTable>
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader(
              selectedAnalysisDocument ? `${selectedAnalysisDocument.title || "Binder File"} AI Review` : "Binder AI Review",
              "Stored analysis results for the latest and previous reviewed versions of this binder document.",
              "pi pi-sparkles",
            )}
            visible={showAnalysisDialog}
            modal
            onHide={() => setShowAnalysisDialog(false)}
            style={{ ...appDialogStyle, width: "75vw", maxWidth: "1000px" }}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            {loadingAnalysisDialog ? (
              <div className="flex justify-content-center p-4">
                <ProgressSpinner />
              </div>
            ) : (
              <div className="flex flex-column gap-3">
                <div style={appDataTableWrapperStyle}>
                  <DataTable value={selectedAnalyses} paginator rows={5} emptyMessage="No AI analyses have been stored for this binder file yet.">
                    <Column field="createdAt" header="Analyzed" body={(rowData: BinderDocumentAnalysis) => formatDateTime(rowData.createdAt)} />
                    <Column field="fileName" header="File" />
                    <Column header="Status" body={(rowData: BinderDocumentAnalysis) => <Tag value={getAnalysisLabel(rowData.status)} severity={getAnalysisSeverity(rowData.status)} />} />
                    <Column field="extractionMethod" header="Extraction" body={(rowData: BinderDocumentAnalysis) => rowData.extractionMethod || "Unknown"} />
                    <Column field="likelyDocumentType" header="Type" body={(rowData: BinderDocumentAnalysis) => rowData.likelyDocumentType || "Unknown"} />
                    <Column header="Folder Match" body={(rowData: BinderDocumentAnalysis) => <Tag value={getFolderMatchLabel(rowData.likelySatisfiesFolder)} severity={getFolderMatchSeverity(rowData.likelySatisfiesFolder)} />} />
                    <Column field="confidenceScore" header="Confidence" body={(rowData: BinderDocumentAnalysis) => rowData.confidenceScore ? `${rowData.confidenceScore}%` : "N/A"} />
                  </DataTable>
                </div>

                {selectedAnalyses[0] && (
                  <div className="grid">
                    <div className="col-12 lg:col-6">
                      <div style={appNeutralPanelStyle}>
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Summary</div>
                        <div style={appMutedTextStyle}>{selectedAnalyses[0].summary || "No analysis summary available."}</div>
                        <div className="mt-3" style={{ fontWeight: 700, color: "#0f172a" }}>Rule Fit</div>
                        <div className="mt-2" style={appMutedTextStyle}>{selectedAnalyses[0].ruleFitRationale || "No explicit rule-fit rationale recorded."}</div>
                        <div className="mt-3" style={{ fontWeight: 700, color: "#0f172a" }}>Recommended Action</div>
                        <div className="mt-2" style={appMutedTextStyle}>{selectedAnalyses[0].recommendedAction || "No action recommended."}</div>
                      </div>
                    </div>
                    <div className="col-12 lg:col-6">
                      <div style={appAccentPanelStyle}>
                        <div style={{ fontWeight: 700, color: "#9a3412", marginBottom: "0.75rem" }}>Evidence Gaps</div>
                        <div style={appMutedTextStyle}>
                          {(selectedAnalyses[0].missingEvidence || []).length > 0
                            ? selectedAnalyses[0].missingEvidence?.join(" | ")
                            : "No missing-evidence flags were recorded."}
                        </div>
                        <div className="mt-3" style={{ fontWeight: 700, color: "#9a3412" }}>Extracted Facts</div>
                        <div className="mt-2" style={appMutedTextStyle}>
                          {(selectedAnalyses[0].extractedFacts || []).length > 0
                            ? selectedAnalyses[0].extractedFacts?.join(" | ")
                            : "No extracted facts were recorded."}
                        </div>
                        <div className="mt-3" style={{ fontWeight: 700, color: "#9a3412" }}>Matching Signals</div>
                        <div className="mt-2" style={appMutedTextStyle}>
                          {(selectedAnalyses[0].matchingSignals || []).length > 0
                            ? selectedAnalyses[0].matchingSignals?.join(" | ")
                            : "No matching signals were recorded."}
                        </div>
                        <div className="mt-3" style={{ fontWeight: 700, color: "#9a3412" }}>Review Reasons</div>
                        <div className="mt-2" style={appMutedTextStyle}>
                          {(selectedAnalyses[0].reviewReasons || []).length > 0
                            ? selectedAnalyses[0].reviewReasons?.join(" | ")
                            : "No review reasons were recorded."}
                        </div>
                        {(selectedAnalyses[0].warnings || []).length > 0 && (
                          <>
                            <div className="mt-3" style={{ fontWeight: 700, color: "#9a3412" }}>Warnings</div>
                            <div className="mt-2" style={appMutedTextStyle}>{selectedAnalyses[0].warnings?.join(" | ")}</div>
                          </>
                        )}
                      </div>
                    </div>
                    <div className="col-12">
                      <div style={appNeutralPanelStyle}>
                        <div style={{ fontWeight: 700, color: "#0f172a", marginBottom: "0.75rem" }}>Extracted Metadata</div>
                        <div style={appMutedTextStyle}>
                          {[
                            selectedAnalyses[0].detectedResidentName ? `Resident: ${selectedAnalyses[0].detectedResidentName}` : "",
                            selectedAnalyses[0].detectedProviderName ? `Provider: ${selectedAnalyses[0].detectedProviderName}` : "",
                            selectedAnalyses[0].detectedDocumentDate ? `Document date: ${selectedAnalyses[0].detectedDocumentDate}` : "",
                            selectedAnalyses[0].detectedEffectiveDate ? `Effective date: ${selectedAnalyses[0].detectedEffectiveDate}` : "",
                            selectedAnalyses[0].detectedExpirationDate ? `Expiration date: ${selectedAnalyses[0].detectedExpirationDate}` : "",
                            selectedAnalyses[0].signatureStatus ? `Signature: ${selectedAnalyses[0].signatureStatus}` : "",
                            selectedAnalyses[0].exactRuleMatchConfidence ? `Rule match confidence: ${selectedAnalyses[0].exactRuleMatchConfidence}%` : "",
                          ].filter(Boolean).join(" | ") || "No extracted metadata was recorded."}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </Dialog>

          <Dialog
            header={renderSurfaceHeader(previewTitle || "Binder Preview", "Preview the selected binder file.", "pi pi-eye")}
            visible={showDocViewer}
            modal
            onHide={() => {
              setShowDocViewer(false);
              setDocViewerImageUrl("");
              setDocViewerNonImageUrl("");
              setDocViewerHtmlContent("");
              setDocViewerSourceUrl("");
              setDocxPreviewBlob(null);
              setLoadingDocxPreview(false);
              setPreviewTitle("");
            }}
            style={{ ...appDialogStyle, width: "75vw", maxWidth: "1000px" }}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            {(loadingPreview || loadingDocxPreview) && (
              <div className="flex justify-content-center p-4">
                <ProgressSpinner />
              </div>
            )}

            {!loadingPreview && !loadingDocxPreview && docViewerNonImageUrl && (
              <iframe key={`preview-iframe-${previewRenderKey}`} title={previewTitle} src={docViewerNonImageUrl} style={{ width: "100%", height: "70vh", border: "1px solid #219d51" }} />
            )}

            {!loadingPreview && !loadingDocxPreview && docViewerHtmlContent && (
              <div
                key={`preview-html-${previewRenderKey}`}
                style={{
                  width: "100%",
                  height: "70vh",
                  overflow: "auto",
                  border: "1px solid #219d51",
                  padding: "1rem",
                  background: "#ffffff",
                }}
                dangerouslySetInnerHTML={{ __html: docViewerHtmlContent }}
              />
            )}

            {!loadingPreview && !loadingDocxPreview && docViewerImageUrl && (
              <div className="flex justify-content-center">
                <img key={`preview-image-${previewRenderKey}`} src={docViewerImageUrl} alt={previewTitle} style={{ width: "100%", height: "70vh", objectFit: "contain", border: "1px solid #219d51" }} />
              </div>
            )}

            {!loadingPreview && docxPreviewBlob && (
              <div
                style={{
                  width: "100%",
                  height: "70vh",
                  overflow: "auto",
                  border: "1px solid #219d51",
                  padding: "1rem",
                  background: "#eef2f7",
                }}
              >
                <div ref={docxPreviewStyleContainerRef} aria-hidden="true" style={{ display: "none" }} />
                <div ref={docxPreviewContainerRef} key={`preview-docx-${previewRenderKey}`} />
              </div>
            )}

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Close"
                className="p-button-outlined"
                onClick={() => {
                  setShowDocViewer(false);
                  setDocViewerImageUrl("");
                  setDocViewerNonImageUrl("");
                  setDocViewerHtmlContent("");
                  setDocViewerSourceUrl("");
                  setDocxPreviewBlob(null);
                  setLoadingDocxPreview(false);
                  setPreviewTitle("");
                }}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Create Custom Folder", "Add a custom binder folder for records that do not fit the system folders.", "pi pi-plus")}
            visible={showCreateFolderDialog}
            modal
            onHide={() => !creatingFolder && setShowCreateFolderDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div style={appFormSectionStyle}>
              <label htmlFor="newBinderFolderName" className="mb-2 block"><strong>Folder Name</strong></label>
              <InputText
                id="newBinderFolderName"
                value={newFolderName}
                onChange={(e) => setNewFolderName(e.target.value)}
                placeholder="Example: Hospital Notes"
                className="w-full"
              />
            </div>

            <div className="mt-3 flex align-items-start gap-2">
              <Checkbox
                inputId="newBinderFolderRequired"
                checked={newFolderRequired}
                onChange={(event) => setNewFolderRequired(Boolean(event.checked))}
              />
              <label htmlFor="newBinderFolderRequired"><strong>Required folder</strong></label>
            </div>

            <div className="mt-3 flex align-items-start gap-2">
              <Checkbox
                inputId="newBinderFolderNotShareable"
                checked={newFolderNotShareable}
                onChange={(event) => setNewFolderNotShareable(Boolean(event.checked))}
              />
              <div>
                <label htmlFor="newBinderFolderNotShareable" className="block"><strong>Private folder</strong></label>
                <small style={appMutedTextStyle}>Keep this folder and its documents private when the Binder is shared with a provider.</small>
              </div>
            </div>

            <div className="mt-4 flex justify-content-end gap-2">
              <Button
                label="Cancel"
                className="p-button-outlined"
                onClick={() => setShowCreateFolderDialog(false)}
                disabled={creatingFolder}
              />
              <Button
                label={creatingFolder ? "Creating..." : "Create"}
                icon="pi pi-check"
                severity="warning"
                onClick={() => void onCreateCustomFolder()}
                disabled={creatingFolder}
              />
            </div>
          </Dialog>

          <Dialog
            header={renderSurfaceHeader("Edit Folder", "Update this folder's requirement setting.", "pi pi-pencil")}
            visible={showEditFolderDialog}
            modal
            onHide={() => !savingFolderProperties && setShowEditFolderDialog(false)}
            style={appDialogStyle}
            headerStyle={appDialogHeaderStyle}
            contentStyle={appDialogContentStyle}
          >
            <div className="flex flex-column gap-3">
              <div>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>{editingFolder?.folderName || "Folder"}</div>
                <small style={appMutedTextStyle}>Required folders are included when Binder rules are run.</small>
              </div>
              <div className="flex align-items-start gap-2">
                <Checkbox
                  inputId="editBinderFolderRequired"
                  checked={editFolderRequired}
                  onChange={(event) => setEditFolderRequired(Boolean(event.checked))}
                />
                <label htmlFor="editBinderFolderRequired"><strong>Required folder</strong></label>
              </div>
              <div className="flex align-items-start gap-2">
                <Checkbox
                  inputId="editBinderFolderPrivate"
                  checked={editFolderPrivate}
                  onChange={(event) => setEditFolderPrivate(Boolean(event.checked))}
                />
                <div>
                  <label htmlFor="editBinderFolderPrivate" className="block"><strong>Private folder</strong></label>
                  <small style={appMutedTextStyle}>Private folders and their files are excluded when the Binder is shared.</small>
                </div>
              </div>
              <div className="flex justify-content-end gap-2">
                <Button
                  label="Cancel"
                  className="p-button-outlined"
                  onClick={() => setShowEditFolderDialog(false)}
                  disabled={savingFolderProperties}
                />
                <Button
                  label={savingFolderProperties ? "Saving..." : "Save"}
                  icon="pi pi-check"
                  severity="warning"
                  onClick={() => void onSaveFolderProperties()}
                  disabled={savingFolderProperties}
                />
              </div>
            </div>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default BinderPage;
